import { DOCUMENT } from '@angular/common';
import { Component, Inject, OnDestroy, OnInit, Renderer2 } from '@angular/core';
import { LoginService } from 'src/app/services/login.service';
import { UserService } from 'src/app/services/user.service';
import { ClaimService } from 'src/app/services/claim.service';
import { HappyReturnsService } from 'src/app/services/happy-returns.service';
import { Router } from '@angular/router';
import { PayeeService } from 'src/app/services/payee.service';
import { ISubmitClaimRequest, IUploadDocumentRequest, IValidateClaimsRequest } from 'src/app/shared/models/claim.interface';
import { ALL_SPECIAL_CHARACTERS } from 'src/app/shared/regex-patterns';
import { environment as ENV } from 'src/environments/environment';
import * as XLSX from 'xlsx';
import * as FileSaver from 'file-saver';
import { HttpResponse } from '@angular/common/http';

interface MultiClaim2Row {
  id: number;
  selected: boolean;
  claimId?: number;
  claimFileId?: number | string;
  trackingNumber: string;
  carrier: string;
  natureOfLoss: string;
  merchandiseDescription: string;
  shipDate: string;
  merchandiseAmount: number;
  shippingAmount: number;
  totalAmount: number;
  additionalComments: string;
  ref1: string;
  creditMemo?: string;
  replacementTrackingNumber?: string;
  draftClaimNo?: string;
  gwccClaimStatus?: string;
  status?: string;
  reasonForFailure?: string;
  remarks?: string;
  isValid?: number;
  documentCount?: number;
  documents: MultiClaim2Document[];
}

interface MultiClaim2Document {
  name: string;
  publicID?: string;
  status?: string;
  uploadedDate?: string;
}

type MultiClaim2FieldKey =
  'trackingNumber'
  | 'carrier'
  | 'natureOfLoss'
  | 'merchandiseDescription'
  | 'shipDate'
  | 'replacementTrackingNumber'
  | 'merchandiseAmount'
  | 'shippingAmount'
  | 'totalAmount'
  | 'creditMemo'
  | 'additionalComments'
  | 'ref1'
  | 'general';

interface MultiClaim2ValidationMessage {
  field: MultiClaim2FieldKey;
  message: string;
}

interface MultiClaim2RecentUpload {
  claimFileId: number | string;
  fileName: string;
  blobGuid: string;
  error: string;
  importStatus: string;
  importedOn: string;
  importedBy: string;
  policyNo: string;
  gwccStatus: string;
  groupCode: string;
}

@Component({
  selector: 'upsc-multi-claim2',
  templateUrl: './multi-claim2.component.html',
  styleUrls: ['./multi-claim2.component.scss']
})
export class MultiClaim2Component implements OnInit, OnDestroy {
  // Toggle this flag to true for local UI testing without calling the API.
  useMockRecentUploads = false;
  private _step = 0;
  selectedFile: File | null = null;
  rows: MultiClaim2Row[] = [];
  parseError = '';
  systemError = '';
  submitError = '';
  reviewActionError = '';
  exportError = '';
  currentSubmissionNumber = '';
  acknowledge = false;
  lastExportFileImportId: number | string | null = null;
  submitExportLoading = false;
  cancelExportLoading = false;
  deleteSelectedLoading = false;
  submitClaimsLoading = false;
  validateClaimsLoading = false;
  private pendingGwccFailureRows: MultiClaim2Row[] = [];
  private validationRetryTimer: ReturnType<typeof setTimeout> | null = null;
  private validationRetryCount = 0;
  private readonly validationRetryDelayMs = 2000;
  private readonly validationRetryLimit = 30;
  private authRecoveryInProgress = false;
  recentUploads: MultiClaim2RecentUpload[] = [];
  historyLoading = false;
  historyError = '';
  contactFirstName = '';
  contactLastName = '';
  contactName = '';
  contactEmail = '';
  contactPhone = '';
  // "I would like to enter a different contact" form fields - kept separate from contact* so they start blank instead of inheriting the recipient/profile prefill.
  altContactFirstName = '';
  altContactLastName = '';
  altContactEmail = '';
  altContactPhone = '';
  recipientName = '';
  recipientEmail = '';
  recipientPhone = '';
  recipientAddressLine1 = '';
  recipientAddressLine2 = '';
  recipientLoading = false;
  useDifferentContact = false;
  reviewFilter: 'all' | 'errors' = 'all';
  pageSize = 10;
  currentPage = 1;
  readonly pageSizeOptions = [10, 25, 50];
  recentSearchText = '';
  recentSearchCategory: 'fileName' | 'submissionNumber' = 'fileName';
  advancedFilterOpen = false;
  advancedStartDate = '';
  advancedEndDate = '';
  advancedImportStatus = '';
  private appliedAdvancedStartDate = '';
  private appliedAdvancedEndDate = '';
  private appliedAdvancedImportStatus = '';
  recentPageSize = 10;
  recentCurrentPage = 1;
  readonly recentPageSizeOptions = [10, 25, 50];
  uploadingClaimIds: number[] = [];
  deletingDocumentIds: string[] = [];
  totalClaimsToPrepare: number | null = null;
  documentsRefreshLoading = false;
  docPageSize = 10;
  docCurrentPage = 1;
  readonly docPageSizeOptions = [10, 25, 50];
  activeDocRowId: number | null = null;
  private documentPanelRequestId = 0;
  isReadOnlyView = false;
  showContinueConfirmModal = false;
  showCancelUploadModal = false;
  editingRowId: number | null = null;
  editingRowLoading = false;
  trackingNumberApiError = '';
  editingDraft: {
    trackingNumber: string;
    carrier: string;
    natureOfLoss: string;
    merchandiseDescription: string;
    shipDate: string;
    replacementTrackingNumber: string;
    merchandiseAmount: string;
    shippingAmount: string;
    totalAmount: string;
    creditMemo: string;
    additionalComments: string;
    ref1: string;
  } | null = null;

  private readonly recentStorageKey = 'multiClaim2RecentUploads';
  private readonly dpatReturnKey = 'mc2AwaitingContactVerification';
  private readonly dpatPayeeKey = 'mc2DpatPayeeId';
  private readonly dpatPayeeNameKey = 'mc2DpatPayeeName';
  // Matches upsc-document-upload's file acceptance rules (size/name-length/duplicate) for consistency across the app.
  private readonly maxDocumentFileSizeBytes = 10000000;
  private readonly maxDocumentFileNameLength = 80;
  private readonly maxSupportingDocumentsPerClaim = 10;
  private readonly documentLimitMessage = 'Maximum 10 supporting documents can be uploaded per claim.';
  private readonly pendingDocumentUploads = new Map<number, number>();
  private readonly mockRecentUploads: MultiClaim2RecentUpload[] = [
    {
      claimFileId: 7561,
      fileName: '15914024-18000 -testing - Copy.xlsx',
      blobGuid: '',
      error: '',
      importStatus: 'PENDING',
      importedOn: '2026-06-01T14:38:00',
      importedBy: 'Jhanvi Desai',
      policyNo: '15914024-P0014548',
      gwccStatus: '',
      groupCode: 'sannathurai@ups.com'
    },
    {
      claimFileId: 7501,
      fileName: 'MultiClaimTest for ONL PROD 2.xlsx',
      blobGuid: '',
      error: '',
      importStatus: 'PENDING',
      importedOn: '2026-05-11T16:33:00',
      importedBy: 'Tedd Cherubin',
      policyNo: '15914024-P0014548',
      gwccStatus: '',
      groupCode: 'sannathurai@ups.com'
    }
  ];

  constructor(
    private claimService: ClaimService,
    private userService: UserService,
    private loginService: LoginService,
    private happyReturnsService: HappyReturnsService,
    private payeeService: PayeeService,
    private router: Router,
    private renderer: Renderer2,
    @Inject(DOCUMENT) private document: Document
  ) {}

  get step(): number {
    return this._step;
  }

  set step(value: number) {
    this._step = value;
    this.syncFlowLayoutMode();
  }

  ngOnInit(): void {
    this.prefillContactDetails();
    this.captureDpatContext();
    this.handleDpatReturn();
    this.loadRecipientDetails();
    this.loadRecentUploads(false);
    this.syncFlowLayoutMode();
  }

  ngOnDestroy(): void {
    this.stopValidationPolling();
    this.renderer.removeClass(this.document.body, 'mc2-fullscreen-flow');
  }

  // Legacy UploadDocument gates Continue on draft claims reaching the original uploaded-claim count.
  get documentsPreparedCount(): number {
    return this.submissionEligibleRows.filter((row) => !!String(row.draftClaimNo || '').trim()).length;
  }

  get documentsTotalCount(): number {
    return this.submissionEligibleRows.length;
  }

  private get submissionEligibleRows(): MultiClaim2Row[] {
    return this.rows.filter((row) => this.isSubmissionEligible(row));
  }

  private isSubmissionEligible(row: MultiClaim2Row): boolean {
    return !this.isReviewRowInvalid(row)
      && !this.isGwccFailureStatus(row.gwccClaimStatus)
      && !this.isGwccCancellationFailure(row)
      && Number(row.isValid) !== 0;
  }

  get submissionEligibleClaimsCount(): number {
    return this.submissionEligibleRows.length;
  }

  get documentsAllPrepared(): boolean {
    const eligibleRows = this.submissionEligibleRows;
    return eligibleRows.length > 0
      && eligibleRows.every((row) => !!String(row.draftClaimNo || '').trim());
  }

  get readinessPercent(): number {
    if (!this.documentsTotalCount) {
      return 0;
    }

    return Math.round((this.documentsPreparedCount / this.documentsTotalCount) * 100);
  }

  private hasUploadedDocument(row: MultiClaim2Row): boolean {
    return this.getDocumentDisplayCount(row) > 0;
  }

  getDocumentDisplayCount(row: MultiClaim2Row): number {
    const documentsInMemory = row && Array.isArray(row.documents) ? row.documents.length : 0;
    const documentsOnServer = row ? Number(row.documentCount || 0) : 0;
    return documentsInMemory || (Number.isFinite(documentsOnServer) && documentsOnServer > 0 ? documentsOnServer : 0);
  }

  // Distinct from readinessPercent: this reflects actual supporting-document attachments,
  // not GWCC draft readiness, so the bar can't read "complete" before any file is selected.
  get uploadProgressPercent(): number {
    if (!this.rows.length) {
      return 0;
    }

    return Math.round((this.claimsWithDocumentsCount / this.rows.length) * 100);
  }

  get claimsWithDocumentsCount(): number {
    return this.rows.filter((row) => this.hasUploadedDocument(row)).length;
  }

  get docRows(): MultiClaim2Row[] {
    const start = (this.docCurrentPage - 1) * this.docPageSize;
    const prioritizedRows = this.rows
      .map((row, index) => ({ row, index }))
      .sort((left, right) => {
        const leftFailed = this.isDraftCreationFailure(left.row) ? 1 : 0;
        const rightFailed = this.isDraftCreationFailure(right.row) ? 1 : 0;
        return rightFailed - leftFailed || left.index - right.index;
      })
      .map((item) => item.row);

    return prioritizedRows.slice(start, start + this.docPageSize);
  }

  private isDraftCreationFailure(row: MultiClaim2Row): boolean {
    return this.isGwccCancellationFailure(row) || this.isReviewRowInvalid(row);
  }

  isDocumentAttachDisabled(row: MultiClaim2Row): boolean {
    return this.isDraftCreationFailure(row);
  }

  getDocumentAttachTitle(row: MultiClaim2Row): string {
    if (this.isGwccCancellationFailure(row)) {
      return 'This claim failed validation or processing and cannot accept documents.';
    }
    return this.isReviewRowInvalid(row)
      ? 'This claim has validation errors and cannot accept documents.'
      : 'Attach documents';
  }

  get docFilteredCount(): number {
    return this.rows.length;
  }

  get activeDocRow(): MultiClaim2Row | null {
    if (this.activeDocRowId === null) {
      return null;
    }

    return this.rows.find((row) => row.id === this.activeDocRowId) || null;
  }

  get docTotalPages(): number {
    return Math.max(1, Math.ceil(this.docFilteredCount / this.docPageSize));
  }

  get docStartIndex(): number {
    if (!this.docFilteredCount) {
      return 0;
    }
    return (this.docCurrentPage - 1) * this.docPageSize + 1;
  }

  get docEndIndex(): number {
    return Math.min(this.docCurrentPage * this.docPageSize, this.docFilteredCount);
  }

  private get filteredReviewRows(): MultiClaim2Row[] {
    return this.reviewFilter === 'errors'
      ? this.rows.filter((row) => this.isReviewRowInvalid(row))
      : this.rows;
  }

  get reviewRows(): MultiClaim2Row[] {
    const start = (this.currentPage - 1) * this.pageSize;
    return this.filteredReviewRows.slice(start, start + this.pageSize);
  }

  get filteredReviewCount(): number {
    return this.filteredReviewRows.length;
  }

  get reviewTotalPages(): number {
    return Math.max(1, Math.ceil(this.filteredReviewCount / this.pageSize));
  }

  get reviewStartIndex(): number {
    if (!this.filteredReviewCount) {
      return 0;
    }
    return (this.currentPage - 1) * this.pageSize + 1;
  }

  get reviewEndIndex(): number {
    return Math.min(this.currentPage * this.pageSize, this.filteredReviewCount);
  }

  get reviewErrorCount(): number {
    return this.rows.filter((row) => this.isReviewRowInvalid(row)).length;
  }

  // Legacy EditClaim.cshtml renders each field's error inline (<span id="errX">) on the edit form itself.
  get editingSourceRow(): MultiClaim2Row | null {
    return this.editingRowId === null ? null : (this.rows.find((row) => row.id === this.editingRowId) || null);
  }

  getEditingFieldMessage(field: MultiClaim2FieldKey): string {
    const draftMessage = this.getEditingDraftFieldMessage(field);
    if (draftMessage !== null) {
      if (field === 'trackingNumber' && !draftMessage && this.trackingNumberApiError) {
        return this.trackingNumberApiError;
      }
      return draftMessage;
    }

    const row = this.editingSourceRow;
    if (!row) {
      return '';
    }
    const messages = this.getReviewFieldValidationMessages(row, field);
    const fieldMessage = messages.find((message) => !this.isGenericUpdateFailureMessage(message));
    return fieldMessage || '';
  }

  private isGenericUpdateFailureMessage(message: string): boolean {
    return /^error in updating claim details\.?$/i.test(String(message || '').trim());
  }

  // Live validation against the CURRENT edit draft (not the stale server remarks) so the Update
  // button can't be re-enabled/disabled based on values the user has already changed on screen.
  readonly natureOfLossOptions = ['Loss', 'Damage', 'Missing Contents', 'Late Delivery'];

  private getEditingDraftFieldMessage(field: MultiClaim2FieldKey): string | null {
    if (!this.editingDraft) {
      return null;
    }

    if (field === 'natureOfLoss') {
      if (this.isHappyReturnsFlow) {
        return null;
      }
      const value = (this.editingDraft.natureOfLoss || '').trim();
      if (!value) {
        return 'Nature Of Loss is required.';
      }
      return this.natureOfLossOptions.some((option) => option.toLowerCase() === value.toLowerCase())
        ? ''
        : 'Nature of Loss must be either "Loss", "Damage", "Missing Contents", or "Late Delivery".';
    }

    if (field === 'shipDate') {
      const value = (this.editingDraft.shipDate || '').trim();
      if (!value) {
        return 'Ship Date is required.';
      }
      const parsed = new Date(value);
      if (isNaN(parsed.getTime())) {
        return null;
      }
      const today = new Date();
      today.setHours(0, 0, 0, 0);
      parsed.setHours(0, 0, 0, 0);
      return parsed.getTime() > today.getTime() ? 'Ship date cannot be a future date.' : '';
    }

    if (field === 'trackingNumber') {
      const trackingNumber = (this.editingDraft.trackingNumber || '').trim();
      if (!trackingNumber) {
        return `${this.isHappyReturnsFlow ? 'Happy Returns ID' : 'Tracking Number'} is required.`;
      }

      const normalizedTrackingNumber = trackingNumber.toLowerCase();
      const duplicateExists = this.rows.some((row) =>
        row.id !== this.editingRowId
        && (row.trackingNumber || '').trim().toLowerCase() === normalizedTrackingNumber
      );

      return duplicateExists ? 'Duplicate Tracking Number' : '';
    }

    if (field === 'carrier') {
      return this.isHappyReturnsFlow ? '' : ((this.editingDraft.carrier || '').trim() ? '' : 'Carrier is required.');
    }

    if (field === 'merchandiseDescription') {
      return (this.editingDraft.merchandiseDescription || '').trim() ? '' : `${this.isHappyReturnsFlow ? 'Product Name' : 'Merchandise Description'} is required.`;
    }

    if (field === 'merchandiseAmount') {
      const value = String(this.editingDraft.merchandiseAmount ?? '').trim();
      if (value && !this.isValidAmountInput(value)) {
        return 'Merchandise Amount must be a valid number (for example, 12 or 12.50).';
      }
      const amount = this.parseAmount(value);
      if (amount < 0) {
        return 'Merchandise Amount cannot be negative.';
      }
      return amount <= 0 ? 'Merchandise Amount must be greater than 0.' : '';
    }

    if (field === 'shippingAmount') {
      if (this.isHappyReturnsFlow) {
        return '';
      }
      const value = String(this.editingDraft.shippingAmount ?? '').trim();
      if (value && !this.isValidAmountInput(value)) {
        return 'Shipping Amount must be a valid number.';
      }
      const amount = this.parseAmount(value);
      if (amount < 0) {
        return 'Shipping Amount cannot be negative.';
      }
      return amount <= 0 ? 'Shipping Amount must be greater than 0.' : '';
    }

    // Total Amount is always auto-calculated/readonly here, so it can never itself be invalid.
    if (field === 'totalAmount') {
      return '';
    }

    return null;
  }

  private readonly editingFieldKeys: MultiClaim2FieldKey[] = [
    'trackingNumber', 'carrier', 'natureOfLoss', 'merchandiseDescription', 'shipDate',
    'replacementTrackingNumber', 'merchandiseAmount', 'shippingAmount', 'totalAmount',
    'creditMemo', 'additionalComments', 'ref1'
  ];

  hasEditingValidationErrors(): boolean {
    if (!this.editingDraft) {
      return false;
    }
    return this.editingFieldKeys.some((field) => !!this.getEditingFieldMessage(field));
  }

  get reviewValidationSummaries(): string[] {
    const invalidRows = this.rows.filter((row) => this.isReviewRowInvalid(row));
    const summaries: string[] = [];

    invalidRows.forEach((row) => {
      const rowLabel = row.trackingNumber && row.trackingNumber.trim()
        ? `Tracking ${row.trackingNumber.trim()}`
        : `Row ${row.id}`;
      this.getReviewValidationMessages(row).forEach((message) => {
        summaries.push(`${rowLabel}: ${message}`);
      });
    });

    return Array.from(new Set(summaries));
  }

  get reviewValidationOverflowCount(): number {
    return 0;
  }

  get filteredRecentUploads(): MultiClaim2RecentUpload[] {
    const term = (this.recentSearchText || '').trim().toLowerCase();
    return this.recentUploads.filter((row) => {
      const matchesSearch = !term || (this.recentSearchCategory === 'submissionNumber'
        ? this.getSubmissionNumberDisplay(row).toLowerCase().includes(term)
        : (row.fileName || '').toLowerCase().includes(term));
      const importedDate = this.getDateOnly(row.importedOn);
      const matchesStart = !this.appliedAdvancedStartDate || importedDate >= this.appliedAdvancedStartDate;
      const matchesEnd = !this.appliedAdvancedEndDate || importedDate <= this.appliedAdvancedEndDate;
      const matchesStatus = !this.appliedAdvancedImportStatus
        || this.getRecentStatusLabel(row.importStatus) === this.appliedAdvancedImportStatus;
      return matchesSearch && matchesStart && matchesEnd && matchesStatus;
    });
  }

  openAdvancedFilter(): void {
    this.advancedFilterOpen = true;
  }

  closeAdvancedFilter(): void {
    this.advancedFilterOpen = false;
  }

  applyAdvancedFilter(): void {
    this.appliedAdvancedStartDate = this.advancedStartDate;
    this.appliedAdvancedEndDate = this.advancedEndDate;
    this.appliedAdvancedImportStatus = this.advancedImportStatus;
    this.advancedFilterOpen = false;
    this.recentCurrentPage = 1;
  }

  resetAdvancedFilter(): void {
    this.advancedStartDate = '';
    this.advancedEndDate = '';
    this.advancedImportStatus = '';
    this.appliedAdvancedStartDate = '';
    this.appliedAdvancedEndDate = '';
    this.appliedAdvancedImportStatus = '';
    this.recentCurrentPage = 1;
  }

  private getDateOnly(value: string): string {
    if (!value) {
      return '';
    }
    const date = new Date(value);
    return isNaN(date.getTime()) ? '' : date.toISOString().slice(0, 10);
  }

  get recentSearchPlaceholder(): string {
    return this.recentSearchCategory === 'submissionNumber' ? 'Submission #' : 'File Name';
  }

  get recentTotalPages(): number {
    return Math.max(1, Math.ceil(this.filteredRecentUploads.length / this.recentPageSize));
  }

  get visibleRecentUploads(): MultiClaim2RecentUpload[] {
    const start = (this.recentCurrentPage - 1) * this.recentPageSize;
    return this.filteredRecentUploads.slice(start, start + this.recentPageSize);
  }

  get recentStartIndex(): number {
    return this.visibleRecentUploads.length ? (this.recentCurrentPage - 1) * this.recentPageSize + 1 : 0;
  }

  get recentEndIndex(): number {
    return (this.recentCurrentPage - 1) * this.recentPageSize + this.visibleRecentUploads.length;
  }

  get contactDisplayName(): string {
    return this.useDifferentContact
      ? `${this.contactFirstName} ${this.contactLastName}`.trim()
      : this.recipientName;
  }

  get contactDisplayEmail(): string {
    return this.useDifferentContact ? this.contactEmail : this.recipientEmail;
  }

  get contactDisplayPhone(): string {
    return this.useDifferentContact ? this.contactPhone : this.recipientPhone;
  }

  get canCancelUpload(): boolean {
    return !!this.getCurrentClaimFileId() || !!this.selectedFile || this.rows.length > 0 || this.step > 2;
  }

  onDragOver(event: DragEvent): void {
    event.preventDefault();
  }

  onDrop(event: DragEvent): void {
    event.preventDefault();
    const droppedFile = event.dataTransfer && event.dataTransfer.files && event.dataTransfer.files.item(0);
    if (droppedFile) {
      this.setSelectedFile(droppedFile);
    }
  }

  onFileChange(event: Event): void {
    const target = event.target as HTMLInputElement;
    const file = target.files && target.files.item(0);
    if (file) {
      this.setSelectedFile(file);
    }
  }

  // MultiClaim2 always routes through DPAT (claim-edit getStarted branches here before the wallet flow).
  onGetStarted(): void {
    this.systemError = '';
    const userDetails: any = this.userService.getUserInfo() || {};
    const userPermission = this.userService.getUserPermissions();
    const policy: any = this.loginService.getPolicyDetails() || {};
    const country = (sessionStorage.getItem('locale') || 'en-US').slice(3, 5) || 'US';
    const paymentMode = sessionStorage.getItem('paymentMode');
    sessionStorage.removeItem('paymentMode');

    const request = {
      action: 'Get Started',
      paymentDirection: 'Claims',
      country,
      paymentMode: paymentMode ? 'check' : '',
      policyIdentifier: policy.policyNumber || '',
      email: this.userService.isGuestUser() ? (sessionStorage.getItem('guestUserEmail') || '') : (userDetails.emailAddress || ''),
      callBackURL: `${ENV.baseUrl.web}${this.router.url.startsWith('/happy-returns/')
        ? (this.router.url.includes('MultiClaim2') ? 'happy-returns/claims/MultiClaim2' : 'happy-returns/claims/MultiClaim')
        : (this.router.url.includes('MultiClaim2') ? 'claims/MultiClaim2' : 'claims/MultiClaim')}`,
      scenario: 'US',
      policyDetails: 'hvcm',
      createdByUserId: userDetails.userId || '',
      guestUserIndicator: this.userService.isGuestUser() ? 'Y' : 'N',
      locale: sessionStorage.getItem('locale') || 'en-US',
      tupssFlow: '',
      policyData: {
        insured: policy.insured || ''
      },
      cudAccess: (userPermission && userPermission.isClaimPayee) || this.userService.isGuestUser() ? true : false
    };

    sessionStorage.setItem(this.dpatReturnKey, 'true');
    this.claimService.getRedirect(request).subscribe(
      (data) => {
        const redirectUrl = data && data.data ? data.data.url : '';
        if (!redirectUrl) {
          this.systemError = 'Unable to start the MultiClaim2 flow right now. Please try again.';
          sessionStorage.removeItem(this.dpatReturnKey);
          return;
        }

        window.location.replace(redirectUrl);
      },
      () => {
        this.systemError = 'Unable to start the MultiClaim2 flow right now. Please try again.';
        sessionStorage.removeItem(this.dpatReturnKey);
      }
    );
  }

  continueFromVerifyContact(): void {
    this.systemError = '';
    if (this.useDifferentContact) {
      if (!this.altContactFirstName.trim() || !this.altContactLastName.trim() || !this.altContactEmail.trim()) {
        this.systemError = 'Please verify your contact information before continuing.';
        return;
      }
      this.contactFirstName = this.altContactFirstName.trim();
      this.contactLastName = this.altContactLastName.trim();
      this.contactEmail = this.altContactEmail.trim();
      this.contactPhone = this.altContactPhone.trim();
      this.contactName = `${this.contactFirstName} ${this.contactLastName}`.trim();
    } else {
      this.contactName = this.recipientName.trim();
      this.contactEmail = this.recipientEmail.trim();
      this.contactPhone = this.recipientPhone.trim();

      if (!this.contactName || !this.contactEmail) {
        this.systemError = 'Please verify your contact information before continuing.';
        return;
      }

      const nameParts = this.contactName.split(/\s+/);
      this.contactFirstName = nameParts.shift() || '';
      this.contactLastName = nameParts.join(' ');
    }

    this.step = 2;
  }

  downloadTemplate(event?: Event): void {
    if (event) {
      event.preventDefault();
    }

    const policyNo = this.getCurrentPolicyNumber();
    if (!policyNo) {
      this.systemError = 'Policy number is missing. Unable to download template right now.';
      return;
    }

    this.systemError = '';
    this.claimService.downloadTemplate(policyNo).subscribe(
      (response) => {
        const contentType = response && response.headers ? (response.headers.get('Content-Type') || 'application/octet-stream') : 'application/octet-stream';
        const blob = new Blob([response.body], { type: contentType });
        FileSaver.saveAs(blob, this.getTemplateFileName(response && response.headers ? response.headers.get('Content-Disposition') : null));
      },
      () => {
        this.systemError = 'Unable to download template right now. Please try again.';
      }
    );
  }

  reviewUpload(): void {
    this.parseError = '';
    this.systemError = '';
    this.lastExportFileImportId = null;

    if (!this.selectedFile) {
      this.parseError = 'Please choose a submission form file before continuing.';
      return;
    }

    const parts = this.selectedFile.name.split('.');
    const ext = parts.length > 1 ? parts[parts.length - 1].toLowerCase() : '';
    if (['csv', 'xls', 'xlsx'].indexOf(ext) === -1) {
      this.parseError = 'Please upload a CSV, XLS, or XLSX file.';
      return;
    }

    const sourceFile = this.selectedFile;
    const reader = new FileReader();
    reader.onload = (e: ProgressEvent<FileReader>) => {
      try {
        const binary = e.target && e.target.result;
        const workbook = XLSX.read(binary, { type: 'binary' });
        const firstSheet = workbook.SheetNames[0];
        const sheet = workbook.Sheets[firstSheet];
        const data: any[] = XLSX.utils.sheet_to_json(sheet, { defval: '' });

        if (!data.length) {
          this.parseError = 'The uploaded file does not contain any rows.';
          return;
        }

        if (data.length > 500) {
          this.parseError = 'The uploaded file cannot exceed 500 records.';
          return;
        }

        this.rows = data.map((row, idx) => {
          const merchandiseAmount = this.parseAmount(this.getFieldValue(row, ['Merchandise Amount', 'Item Price', 'MerchandiseAmount', 'ITEMPRICE']));
          const shippingAmount = this.parseAmount(this.getFieldValue(row, ['Shipping Amount', 'ShippingAmount']));
          // Total Amount is always the sum, never the raw imported column - a stale/unrelated value in the file must not win.
          const totalAmount = merchandiseAmount + shippingAmount;

          return {
            id: idx + 1,
            selected: false,
            trackingNumber: String(this.getFieldValue(row, ['Tracking Number', 'Happy Returns ID', 'TrackingNumber']) || ''),
            carrier: String(this.getFieldValue(row, ['Carrier', 'Transportation Carrier', 'TransportationCarrier']) || ''),
            natureOfLoss: String(this.getFieldValue(row, ['Nature Of Loss', 'NatureOfLoss', 'NatureofLoss']) || ''),
            merchandiseDescription: String(this.getFieldValue(row, ['Merchandise Description', 'Product Name', 'MerchandiseDescription']) || ''),
            shipDate: this.normalizeShipDate(this.getFieldValue(row, ['Ship Date', 'Dropped Off Date', 'ShipDate'])),
            replacementTrackingNumber: String(this.getFieldValue(row, ['Replacement Tracking Number', 'ReplacementTrackingNumber']) || ''),
            merchandiseAmount,
            shippingAmount,
            totalAmount,
            creditMemo: String(this.getFieldValue(row, ['Credit Memo', 'CreditMemo']) || ''),
            additionalComments: String(this.getFieldValue(row, ['Additional Comments', 'AdditionalComments']) || ''),
            ref1: String(this.getFieldValue(row, ['Ref1', 'REF1', 'Happy Returns Retailer ID']) || ''),
            documents: []
          } as MultiClaim2Row;
        });

        this.currentSubmissionNumber = '';
        this.reviewFilter = 'all';
        this.pageSize = 10;
        this.currentPage = 1;

        // Upload the validated source workbook unchanged so the API receives the exact template
        // headers and partner-specific columns it is designed to parse.
        this.uploadFileToBackend(sourceFile);
      } catch {
        this.parseError = 'Unable to parse this file. Please verify the template and try again.';
      }
    };

    reader.readAsBinaryString(sourceFile);
  }

  private uploadFileToBackend(file: File): void {
    const context = this.buildUserDataContext();
    const userData = {
      policyNo: context.PolicyNo,
      payeeId: context.PayeeID,
      userName: context.UserName,
      payeeName: context.PayeeName,
      groupCode: context.GroupCode,
      claimSource: context.ClaimSource
    };

    this.claimService.uploadFile([file], userData).subscribe(
      (response) => {
        const message = response && response.message ? String(response.message).split('~')[0].trim() : '';
        const failed = !!message && message.toLowerCase().indexOf('data uploaded successfully') === -1;
        const extractedId = this.extractClaimFileIdFromResponse(response);

        if (failed || !extractedId) {
          this.parseError = message || 'Unable to import this file. Please verify the template and try again.';
          return;
        }

        this.lastExportFileImportId = extractedId;
        this.loadReviewRowsWithValidation(extractedId);
      },
      (error) => {
        const unwrappedError = this.unwrapError(error);
        console.error('❌ File upload failed:', unwrappedError);
        this.parseError = this.extractServerMessage(unwrappedError) || 'Failed to upload file to server. Please try again.';
      }
    );
  }

  private createNormalizedUploadFile(data: any[], sourceFile: File, extension: string): File {
    const rows = data.map((row) => {
      const sku = this.isHappyReturnsFlow ? this.getFieldValue(row, ['SKU', 'Sku']) ?? '' : '';
      const additionalComments = this.getFieldValue(row, ['Additional Comments', 'ADDITIONALCOMMENTS', 'AdditionalComments']) ?? '';

      return {
        TrackingNumber: this.getFieldValue(row, ['Tracking Number', 'Happy Returns ID', 'TrackingNumber']) ?? '',
        TransportationCarrier: this.getFieldValue(row, ['Carrier', 'Transportation Carrier', 'TransportationCarrier']) ?? (this.isHappyReturnsFlow ? 'UPS' : ''),
        NatureOfLoss: this.getFieldValue(row, ['Nature Of Loss', 'NatureOfLoss', 'NatureofLoss']) ?? (this.isHappyReturnsFlow ? 'Loss' : ''),
        MerchandiseDescription: this.getFieldValue(row, ['Merchandise Description', 'Product Name', 'MerchandiseDescription']) ?? '',
        ShipDate: this.getFieldValue(row, ['Ship Date', 'Dropped Off Date', 'ShipDate']) ?? '',
        ReplacementTrackingNumber: this.getFieldValue(row, ['Replacement Tracking Number', 'ReplacementTrackingNumber']) ?? '',
        MerchandiseAmount: this.getFieldValue(row, ['Merchandise Amount', 'Item Price', 'MerchandiseAmount', 'ITEMPRICE']) ?? '',
        ShippingAmount: this.getFieldValue(row, ['Shipping Amount', 'ShippingAmount']) ?? (this.isHappyReturnsFlow ? 0 : ''),
        TotalAmount: this.isHappyReturnsFlow
          ? this.parseAmount(this.getFieldValue(row, ['Item Price', 'ITEM_PRICE', 'Merchandise Amount', 'MerchandiseAmount', 'ITEMPRICE']))
          : this.getFieldValue(row, ['Total Amount', 'TotalAmount']) ?? '',
        CreditMemo: this.getFieldValue(row, ['Credit Memo', 'CreditMemo']) ?? '',
        AdditionalComments: sku ? `${additionalComments};${sku}` : additionalComments,
        Ref1: this.getFieldValue(row, ['Ref1', 'REF1', 'Happy Returns Retailer ID']) ?? ''
      };
    });
    const worksheet = XLSX.utils.json_to_sheet(rows);

    if (extension === 'csv') {
      return new File([XLSX.utils.sheet_to_csv(worksheet)], sourceFile.name, {
        type: sourceFile.type || 'text/csv',
        lastModified: sourceFile.lastModified
      });
    }

    const workbook = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(workbook, worksheet, 'Claims');
    const dataType = extension === 'xls' ? 'xls' : 'xlsx';
    const contents = XLSX.write(workbook, { bookType: dataType, type: 'array' });
    const contentType = extension === 'xls'
      ? 'application/vnd.ms-excel'
      : 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet';
    return new File([contents], sourceFile.name, { type: contentType, lastModified: sourceFile.lastModified });
  }

  // Legacy's List page renders errors immediately on load using GetClaimDetails (remarks/isValid);
  // fetch that same data right after upload instead of waiting for the Continue -> validateClaims call.
  // refreshData returns {response:[]} in this environment; multiClaim/list (getReadOnlyList) is confirmed to work.
  private loadReviewRowsWithValidation(claimFileId: number): void {
    this.claimService.getReadOnlyList(claimFileId, this.getCurrentPolicyNumber()).subscribe(
      (response) => {
        const validatedRows = this.mapRefreshDataRows(response);
        if (validatedRows.length) {
          this.rows = validatedRows;
        }
        this.currentPage = 1;
        this.clampReviewPage();
        this.step = 3;
      },
      (error) => {
        if (this.handleAuthFailure(error)) {
          return;
        }
        // Keep the locally-parsed rows so the user can still proceed if the server check is unavailable.
        this.step = 3;
      }
    );
  }

  private extractServerMessage(error: any): string {
    const body = this.parseResponseBody(error);
    if (!body) {
      return '';
    }

    const message = body.message || body.Message || '';
    return String(message).split('~')[0].trim();
  }

  private extractClaimFileIdFromResponse(response: any): number | null {
    try {
      // Backend may return the id inline in the message ("Data Uploaded Successfully!~[ID]").
      if (response && response.message) {
        const parts = String(response.message).split('~');
        if (parts.length > 1) {
          const id = Number(parts[parts.length - 1].trim());
          if (Number.isFinite(id) && id > 0) {
            return id;
          }
        }
      }

      const candidates = [response, response && response.data, response && response.response];
      const keys = ['claimFileId', 'claimFileID', 'ClaimFileId', 'ClaimFileID', 'importFileID', 'importFileId', 'id', 'ID'];

      for (const source of candidates) {
        if (!source || typeof source !== 'object') {
          continue;
        }

        for (const key of keys) {
          const id = Number(source[key]);
          if (Number.isFinite(id) && id > 0) {
            return id;
          }
        }
      }

      return null;
    } catch (error) {
      console.error('Error extracting claimFileId:', error);
      return null;
    }
  }

  deleteSelectedRows(): void {
    this.systemError = '';
    if (this.validateClaimsLoading || this.deleteSelectedLoading) {
      return;
    }

    const selectedRows = this.rows.filter((row) => row.selected);
    if (!selectedRows.length) {
      this.systemError = 'Please select at least one record to delete.';
      return;
    }

    const claimFileId = this.getCurrentClaimFileId();
    const selectedRecordIds = selectedRows
      .map((row) => Number(row.claimId))
      .filter((id) => Number.isFinite(id) && id > 0);

    const canCallDeleteApi = !!claimFileId
      && selectedRecordIds.length > 0
      && selectedRecordIds.length === selectedRows.length;

    if (!canCallDeleteApi) {
      this.rows = this.rows.filter((row) => !row.selected);
      this.clearEmptySubmissionState();
      this.clampReviewPage();
      return;
    }

    this.deleteSelectedLoading = true;
    this.claimService.deleteSelected(selectedRecordIds, claimFileId, this.getCurrentPolicyNumber()).subscribe(
      (response) => {
        this.deleteSelectedLoading = false;
        const responseMessage = response && response.message ? String(response.message).toLowerCase() : 'success';
        if (responseMessage !== 'success') {
          this.systemError = 'Unable to delete selected records right now. Please try again.';
          return;
        }

        const serverRows = this.mapRefreshDataRows(response);
        if (serverRows.length) {
          this.rows = serverRows;
          this.currentPage = 1;
          this.clampReviewPage();
          return;
        }

        this.rows = this.rows.filter((row) => !row.selected);
        this.clearEmptySubmissionState();
        this.clampReviewPage();
      },
      (error) => {
        this.deleteSelectedLoading = false;
        if (this.handleAuthFailure(error)) {
          return;
        }
        this.systemError = this.extractServerMessage(error) || 'Unable to delete selected records right now. Please try again.';
      }
    );
  }

  toggleSelectAll(event: Event): void {
    if (this.validateClaimsLoading) {
      return;
    }

    const checked = (event.target as HTMLInputElement).checked;
    // Apply to the active filter across all pages, leaving hidden rows unchanged.
    this.filteredReviewRows.forEach((row) => { row.selected = checked; });
  }

  get allRowsSelected(): boolean {
    const rows = this.filteredReviewRows;
    return rows.length > 0 && rows.every((row) => row.selected);
  }

  get someRowsSelected(): boolean {
    const rows = this.filteredReviewRows;
    const selectedCount = rows.filter((row) => row.selected).length;
    return selectedCount > 0 && selectedCount < rows.length;
  }

  setReviewFilter(filter: 'all' | 'errors'): void {
    this.reviewFilter = filter;
    this.currentPage = 1;
  }

  onPageSizeChange(event: Event): void {
    const value = Number((event.target as HTMLSelectElement).value || 10);
    this.pageSize = this.pageSizeOptions.indexOf(value) > -1 ? value : 10;
    this.currentPage = 1;
  }

  onRecentPageSizeChange(event: Event): void {
    const value = Number((event.target as HTMLSelectElement).value || 10);
    this.recentPageSize = this.recentPageSizeOptions.indexOf(value) > -1 ? value : 10;
    this.recentCurrentPage = 1;
  }

  goToRecentPage(page: number): void {
    if (page < 1 || page > this.recentTotalPages) {
      return;
    }
    this.recentCurrentPage = page;
  }

  goToReviewPage(page: number): void {
    if (page < 1 || page > this.reviewTotalPages) {
      return;
    }
    this.currentPage = page;
  }

  goToDocPage(page: number): void {
    if (page < 1 || page > this.docTotalPages) {
      return;
    }
    this.docCurrentPage = page;
  }

  onDocPageSizeChange(event: Event): void {
    const value = Number((event.target as HTMLSelectElement).value || 10);
    this.docPageSize = this.docPageSizeOptions.indexOf(value) > -1 ? value : 10;
    this.docCurrentPage = 1;
  }

  toggleDocRow(row: MultiClaim2Row): void {
    if (this.isDocumentAttachDisabled(row)) {
      return;
    }

    this.systemError = '';
    this.activeDocRowId = this.activeDocRowId === row.id ? null : row.id;
    const requestId = ++this.documentPanelRequestId;

    const claimId = this.getValidClaimId(row);
    const claimFileId = this.resolvePositiveIdentifier(row.claimFileId) ?? this.resolvePositiveIdentifier(this.getCurrentClaimFileId());
    if (this.activeDocRowId !== row.id || claimId === null || claimFileId === null) {
      return;
    }

    this.claimService.getUploadedDocumentsDetails(claimFileId).subscribe(
      (response) => {
        if (requestId !== this.documentPanelRequestId || this.activeDocRowId !== row.id
          || this.getValidClaimId(row) !== claimId) {
          return;
        }

        // This endpoint is scoped to ClaimFileId (the entire submission), not one claim.
        // Never assign another claim's records, or records without an owner, to this row.
        const items = Array.isArray(response) ? response
          : (response && Array.isArray(response.data) ? response.data
            : (response && Array.isArray(response.response) ? response.response : []));
        const claimDocuments = items.filter((item: any) =>
          this.resolvePositiveIdentifier(this.getFieldValue(item, ['claimId', 'ClaimId', 'claimID', 'ClaimID'])) === claimId
        );
        row.documents = this.mapSubDocumentRows(claimDocuments);
        row.documentCount = row.documents.length;
      },
      () => {
        // Keep the existing row when the optional detail refresh is unavailable.
      }
    );
  }

  closeDocPanel(): void {
    this.systemError = '';
    this.activeDocRowId = null;
    ++this.documentPanelRequestId;
  }

  // Driven by the same messages as the row tooltip so the banner count can never disagree with it.
  isReviewRowInvalid(row: MultiClaim2Row): boolean {
    return this.getReviewValidationItems(row).length > 0;
  }

  isTrackingNumberInvalid(row: MultiClaim2Row): boolean {
    return this.hasReviewFieldError(row, 'trackingNumber');
  }

  getReasonForFailure(row: MultiClaim2Row): string {
    const remarks = String(row && (row.reasonForFailure || row.remarks) ? (row.reasonForFailure || row.remarks) : '').trim();
    if (!remarks) {
      if (this.isGwccFailureStatus(row && row.gwccClaimStatus)) {
        return 'Claim failed validation or processing; no reason was provided.';
      }
      return '-';
    }

    const normalized = remarks.toLowerCase();
    if (normalized === 'success' || normalized === 'submitted') {
      return '-';
    }

    const segments = remarks
      .split('~')
      .map((segment) => String(segment || '').trim())
      .filter((segment) => !!segment);

    return segments.length ? segments.join('; ') : '-';
  }

  getReviewValidationMessages(row: MultiClaim2Row): string[] {
    return this.getReviewValidationItems(row).map((item) => item.message);
  }

  getReviewFieldValidationMessages(row: MultiClaim2Row, field: MultiClaim2FieldKey): string[] {
    const unique = new Set<string>();
    this.getReviewValidationItems(row)
      .filter((item) => item.field === field)
      .forEach((item) => unique.add(item.message));

    return Array.from(unique);
  }

  hasReviewFieldError(row: MultiClaim2Row, field: MultiClaim2FieldKey): boolean {
    return this.getReviewFieldValidationMessages(row, field).length > 0;
  }

  // Legacy renders "Please Enter" in place of the value when a required field is empty.
  isReviewFieldMissing(row: MultiClaim2Row, field: MultiClaim2FieldKey): boolean {
    return this.getReviewFieldValidationMessages(row, field).some((message) => /is required/i.test(message));
  }

  private getReviewValidationItems(row: MultiClaim2Row): MultiClaim2ValidationMessage[] {
    const serverItems = this.getServerValidationItems(row);
    const clientItems = this.getClientValidationItems(row);
    const merged = [...serverItems, ...clientItems]
      // Total Amount is derived/read-only - never surface a stale server-echoed "required" message for it.
      .filter((item) => item.field !== 'totalAmount')
      .map((item) => ({
        field: item.field,
        message: this.applyLegacyValidationLabelOverrides(item.message)
      }))
      .filter((item) => !!item.message);

    const uniqueKeys = new Set<string>();
    const deduped: MultiClaim2ValidationMessage[] = [];

    merged.forEach((item) => {
      const key = `${item.field}|${item.message.toLowerCase()}`;
      if (uniqueKeys.has(key)) {
        return;
      }

      uniqueKeys.add(key);
      deduped.push(item);
    });

    if (!deduped.length && this.hasUnresolvedServerFailure(row)) {
      deduped.push({ field: 'general', message: 'This record could not be validated. Please update this record and try again.' });
    }

    return deduped;
  }

  private getClientValidationItems(row: MultiClaim2Row): MultiClaim2ValidationMessage[] {
    const messages: MultiClaim2ValidationMessage[] = [];

    if (!row) {
      return [{ field: 'general', message: 'Please update this record and try validating again.' }];
    }

    if (!row.trackingNumber || !row.trackingNumber.trim()) {
      messages.push({ field: 'trackingNumber', message: 'Tracking Number is required.' });
    }
    if (!row.carrier || !row.carrier.trim()) {
      messages.push({ field: 'carrier', message: 'Carrier is required.' });
    }
    if (!row.natureOfLoss || !row.natureOfLoss.trim()) {
      messages.push({ field: 'natureOfLoss', message: 'Nature Of Loss is required.' });
    }
    if (!row.merchandiseDescription || !row.merchandiseDescription.trim()) {
      messages.push({ field: 'merchandiseDescription', message: 'Merchandise Description is required.' });
    }
    if (!row.shipDate || !row.shipDate.trim()) {
      messages.push({ field: 'shipDate', message: 'Ship Date is required.' });
    }

    const merch = Number(row.merchandiseAmount || 0);
    const ship = Number(row.shippingAmount || 0);

    if (merch < 0) {
      messages.push({ field: 'merchandiseAmount', message: 'Merchandise Amount cannot be negative.' });
    }
    if (ship < 0) {
      messages.push({ field: 'shippingAmount', message: 'Shipping Amount cannot be negative.' });
    }
    // Total Amount is derived (Merchandise + Shipping) and never directly editable - never flag it here.

    return messages;
  }

  // A backend failure flag with no remark left to show; without this the row would be counted but list no errors.
  private hasUnresolvedServerFailure(row: MultiClaim2Row): boolean {
    if (!row || this.isValidationClearedStatus(row)) {
      return false;
    }

    if (this.isGwccFailureStatus(row.gwccClaimStatus)) {
      return true;
    }

    return row.isValid !== undefined && row.isValid !== null && Number(row.isValid) === 0;
  }

  private isGwccFailureStatus(status: string | undefined): boolean {
    const normalized = String(status || '').trim().toLowerCase();
    return normalized === 'failed' || normalized === 'error';
  }

  // Legacy displayTotalErrors ignores remarks once a row has been re-saved or processed.
  private isValidationClearedStatus(row: MultiClaim2Row): boolean {
    const status = String(row && row.status ? row.status : '').trim().toLowerCase();
    return status === 'modified but not processed' || status === 'processed';
  }

  private getServerValidationItems(row: MultiClaim2Row): MultiClaim2ValidationMessage[] {
    if (!row || this.isValidationClearedStatus(row)) {
      return [];
    }

    const serverRemark = String(row.remarks || row.reasonForFailure || '').trim();
    if (!serverRemark) {
      return [];
    }

    const normalized = serverRemark.toLowerCase();
    if (normalized === 'success' || normalized === 'submitted') {
      return [];
    }

    const items = serverRemark
      .split('~')
      .map((segment) => String(segment || '').trim())
      .filter((segment) => !!segment)
      .map((segment) => this.parseServerValidationSegment(segment))
      .filter((item) => !!item.message);

    const numericValidationItems = items
      .filter((item) => item.field === 'general' && this.isNumericAmountValidationMessage(item.message))
      .reduce((amountItems, item) => {
        if (Number(row.merchandiseAmount || 0) <= 0) {
          amountItems.push({ field: 'merchandiseAmount', message: `Merchandise Amount: ${item.message}` });
        }
        if (Number(row.shippingAmount || 0) <= 0) {
          amountItems.push({ field: 'shippingAmount', message: `Shipping Amount: ${item.message}` });
        }
        return amountItems;
      }, [] as MultiClaim2ValidationMessage[]);

    const expandedItems = items.filter((item) => !(
      item.field === 'general' && this.isNumericAmountValidationMessage(item.message)
    )).concat(numericValidationItems);

    const uniqueKeys = new Set<string>();
    const deduped: MultiClaim2ValidationMessage[] = [];
    expandedItems.forEach((item) => {
      const key = `${item.field}|${item.message.toLowerCase()}`;
      if (uniqueKeys.has(key)) {
        return;
      }

      uniqueKeys.add(key);
      deduped.push(item);
    });

    return deduped;
  }

  private isNumericAmountValidationMessage(message: string): boolean {
    const normalized = String(message || '').toLowerCase();
    return normalized.indexOf('numeric characters') > -1
      || normalized.indexOf('greater than 0') > -1;
  }

  private parseServerValidationSegment(segment: string): MultiClaim2ValidationMessage {
    const firstComma = segment.indexOf(',');
    const fieldText = firstComma > -1 ? segment.slice(0, firstComma).trim() : '';
    const messageText = firstComma > -1 ? segment.slice(firstComma + 1).trim() : segment.trim();

    // GWCC sometimes puts an identifier (not a field name) before the comma, so fall back to the message text.
    const fieldFromToken = this.mapValidationField(fieldText);

    return {
      field: fieldFromToken === 'general' ? this.mapValidationField(messageText) : fieldFromToken,
      message: messageText || segment.trim()
    };
  }

  private mapValidationField(text: string): MultiClaim2FieldKey {
    const normalized = String(text || '').toLowerCase();
    if (!normalized) {
      return 'general';
    }

    // The policy period rejection names the date even when it also quotes the tracking number.
    if (normalized.indexOf('policy period') > -1 && normalized.indexOf('date') > -1) {
      return 'shipDate';
    }
    if (normalized.indexOf('tracking') > -1 || normalized.indexOf('happy return id') > -1) {
      return 'trackingNumber';
    }
    if (normalized.indexOf('carrier') > -1) {
      return 'carrier';
    }
    if (normalized.indexOf('nature of loss') > -1 || normalized.indexOf('natureofloss') > -1) {
      return 'natureOfLoss';
    }
    if (normalized.indexOf('merchandise description') > -1 || normalized.indexOf('product name') > -1) {
      return 'merchandiseDescription';
    }
    if (normalized.indexOf('ship date') > -1 || normalized.indexOf('loss date') > -1 || normalized.indexOf('dropped off date') > -1) {
      return 'shipDate';
    }
    if (normalized.indexOf('replacement tracking') > -1) {
      return 'replacementTrackingNumber';
    }
    if (normalized.indexOf('merchandise amount') > -1 || normalized.indexOf('item price') > -1) {
      return 'merchandiseAmount';
    }
    if (normalized.indexOf('shipping amount') > -1) {
      return 'shippingAmount';
    }
    if (normalized.indexOf('total amount') > -1) {
      return 'totalAmount';
    }
    if (normalized.indexOf('credit memo') > -1) {
      return 'creditMemo';
    }
    if (normalized.indexOf('additional comments') > -1) {
      return 'additionalComments';
    }
    if (normalized.indexOf('ref1') > -1 || normalized.indexOf('ref 1') > -1) {
      return 'ref1';
    }

    return 'general';
  }

  private applyLegacyValidationLabelOverrides(message: string): string {
    if (!message) {
      return '';
    }

    let normalizedMessage = message.trim();
    const hasDuplicateTracking = /duplicate tracking number/i.test(normalizedMessage);
    const hasExistingClaimMessage = /claim already exists for this tracking number/i.test(normalizedMessage);

    if (hasDuplicateTracking || hasExistingClaimMessage) {
      if (!hasDuplicateTracking) {
        normalizedMessage = `Duplicate Tracking number ${normalizedMessage}`;
      }
      if (!hasExistingClaimMessage) {
        normalizedMessage += ' A claim already exists for this tracking number.';
      }
      normalizedMessage = normalizedMessage.replace(/\s*Please remove this record to proceed\.?/i, '');
    }

    if (!this.isHappyReturnsLegacyPolicy()) {
      return normalizedMessage;
    }

    return normalizedMessage
      .replace(/Tracking\s*number/gi, 'Happy Return ID')
      .replace(/Ship Date/gi, 'Dropped Off Date')
      .replace(/Merchandise Amount/gi, 'Item Price')
      .replace(/Merchandise Description/gi, 'Product Name');
  }

  get isHappyReturnsFlow(): boolean {
    return this.isHappyReturnsLegacyPolicy();
  }

  private isHappyReturnsLegacyPolicy(): boolean {
    return this.getCurrentPolicyNumber() === '15914024-P0022341';
  }

  startEditRow(row: MultiClaim2Row): void {
    if (this.validateClaimsLoading) {
      return;
    }

    this.systemError = '';
    this.trackingNumberApiError = '';
    this.editingRowId = row.id;
    this.editingDraft = {
      trackingNumber: row.trackingNumber || '',
      carrier: row.carrier || '',
      natureOfLoss: this.normalizeNatureOfLoss(row.natureOfLoss),
      merchandiseDescription: row.merchandiseDescription || '',
      shipDate: this.toApiDate(row.shipDate || ''),
      replacementTrackingNumber: row.replacementTrackingNumber || '',
      merchandiseAmount: String(row.merchandiseAmount !== undefined && row.merchandiseAmount !== null ? row.merchandiseAmount : ''),
      shippingAmount: String(row.shippingAmount !== undefined && row.shippingAmount !== null ? row.shippingAmount : ''),
      totalAmount: '',
      creditMemo: row.creditMemo || '',
      additionalComments: row.additionalComments || '',
      ref1: row.ref1 || ''
    };
    this.recalculateEditingTotalAmount();
  }

  // Total Amount is always Merchandise Amount + Shipping Amount - never independently editable.
  recalculateEditingTotalAmount(): void {
    if (!this.editingDraft) {
      return;
    }
    const total = this.parseAmount(this.editingDraft.merchandiseAmount) + this.parseAmount(this.editingDraft.shippingAmount);
    this.editingDraft.totalAmount = total.toFixed(2);
  }

  // Matches the row's saved value to the dropdown's canonical casing so an existing valid value pre-selects correctly.
  private normalizeNatureOfLoss(value: string | undefined | null): string {
    const trimmed = (value || '').trim();
    const match = this.natureOfLossOptions.find((option) => option.toLowerCase() === trimmed.toLowerCase());
    return match || trimmed;
  }

  cancelEditRow(): void {
    this.editingRowId = null;
    this.editingDraft = null;
    this.editingRowLoading = false;
    this.trackingNumberApiError = '';
  }

  clearTrackingNumberApiError(): void {
    this.trackingNumberApiError = '';
  }

  saveActiveEditedRow(): void {
    if (this.editingRowId === null || this.editingRowId === undefined) {
      return;
    }

    const row = this.rows.find((entry) => entry.id === this.editingRowId);
    if (!row) {
      this.systemError = 'Unable to find the selected row for editing. Please try again.';
      return;
    }

    this.saveEditedRow(row);
  }

  saveEditedRow(row: MultiClaim2Row): void {
    if (!this.editingDraft || this.editingRowLoading) {
      return;
    }

    if (this.hasEditingValidationErrors()) {
      return;
    }

    const updatedRow: MultiClaim2Row = {
      ...row,
      trackingNumber: (this.editingDraft.trackingNumber || '').trim(),
      carrier: (this.editingDraft.carrier || '').trim(),
      natureOfLoss: (this.editingDraft.natureOfLoss || '').trim(),
      merchandiseDescription: (this.editingDraft.merchandiseDescription || '').trim(),
      shipDate: this.formatEditedShipDate((this.editingDraft.shipDate || '').trim()),
      replacementTrackingNumber: (this.editingDraft.replacementTrackingNumber || '').trim(),
      merchandiseAmount: this.parseAmount(this.editingDraft.merchandiseAmount),
      shippingAmount: this.parseAmount(this.editingDraft.shippingAmount),
      totalAmount: this.parseAmount(this.editingDraft.merchandiseAmount) + this.parseAmount(this.editingDraft.shippingAmount),
      creditMemo: (this.editingDraft.creditMemo || '').trim(),
      additionalComments: (this.editingDraft.additionalComments || '').trim(),
      ref1: (this.editingDraft.ref1 || '').trim(),
      remarks: '',
      reasonForFailure: '',
      isValid: 1,
      gwccClaimStatus: ''
    };

    this.systemError = '';

    const claimId = Number(updatedRow.claimId);
    const claimFileId = Number(updatedRow.claimFileId || this.getCurrentClaimFileId());
    const canPersistServerSide = Number.isFinite(claimId) && claimId > 0 && Number.isFinite(claimFileId) && claimFileId > 0;

    if (!canPersistServerSide) {
      this.rows = this.rows.map((entry) => entry.id === row.id ? updatedRow : entry);
      this.cancelEditRow();
      return;
    }

    this.editingRowLoading = true;
    this.claimService.updateClaimDetails(this.buildUpdateClaimPayload(updatedRow, claimId, claimFileId)).subscribe(
      (response) => {
        this.editingRowLoading = false;
        const message = response && response.message ? String(response.message) : '';
        if (message && message.toLowerCase().indexOf('success') === -1) {
          if (message.toLowerCase().includes('duplicate tracking number')) {
            this.trackingNumberApiError = message;
            return;
          }
          this.systemError = message;
          return;
        }

        const rowsFromResponse = this.mapRefreshDataRows(response);
        if (rowsFromResponse.length) {
          this.rows = rowsFromResponse.map((responseRow) => {
            const responseClaimId = this.resolvePositiveIdentifier(responseRow.claimId);
            const isUpdatedRow = responseClaimId === claimId || (rowsFromResponse.length === 1 && responseClaimId === null);
            return isUpdatedRow
              ? { ...responseRow, remarks: '', reasonForFailure: '', isValid: 1, gwccClaimStatus: '' }
              : responseRow;
          });
        } else {
          this.rows = this.rows.map((entry) => entry.id === row.id ? updatedRow : entry);
        }
        this.reviewFilter = 'all';
        this.reviewActionError = '';
        this.currentPage = 1;
        this.clampReviewPage();
        this.cancelEditRow();
      },
      (error) => {
        this.editingRowLoading = false;
        const message = this.extractServerMessage(error);
        if (message.toLowerCase().includes('duplicate tracking number')) {
          this.trackingNumberApiError = message;
          return;
        }
        this.systemError = message || 'Unable to save changes. Your original claim data has been kept.';
      }
    );
  }

  // Legacy validates on Continue from the review list, before the documents step.
  // Legacy pops "Would you like to continue?" (#confirm-Modal) before validating on Continue.
  continueToDocuments(): void {
    if (!this.rows.length) {
      this.systemError = 'Please keep at least one row to continue.';
      return;
    }

    if (this.editingRowId !== null) {
      this.systemError = 'Please select Update to save your changes, or Cancel, before continuing.';
      return;
    }

    this.systemError = '';
    this.showContinueConfirmModal = true;
  }

  closeContinueConfirmModal(): void {
    this.showContinueConfirmModal = false;
  }

  confirmContinueToDocuments(): void {
    this.showContinueConfirmModal = false;
    this.pendingGwccFailureRows = [];
    this.stopValidationPolling();

    const claimFileId = this.resolvePositiveIdentifier(this.getCurrentClaimFileId());
    if (claimFileId === null) {
      this.systemError = 'No upload request ID is available. Please re-upload the file.';
      return;
    }

    this.validateClaimsLoading = true;
    this.runValidateClaims(claimFileId);
  }

  //Validate Claims API basically just submits these records for processing. At this time, GW will be in the process of creating draft claim numbers for all the records.
  //In GW, the creation of draft claim numbers may take some time, and if many users are uploading many records for draft claim creation, this process can take even longer due to processing limitations on GW side.
  private runValidateClaims(claimFileId: number): void {
    this.claimService.validateClaims(claimFileId, this.buildValidateClaimsPayload()).subscribe(
      (response) => {
        // if (this.isValidationInProgress(response)) {
        //   this.scheduleValidationRetry(claimFileId);
        //   return;
        // }

        // this.stopValidationPolling();
        const message = response && response.message ? String(response.message).toLowerCase() : '';
        const isSuccess = response && response.isServerError !== true && (
          response.success === true
          || (response.isServerError === false && message === 'success')
          || message === 'success'
        );

        if (!isSuccess) {
          if (this.hasTerminalGwccCancellation(response)) {
            this.captureGwccFailureRows(response);
            this.loadDocumentStep(claimFileId);
            return;
          }
          this.validateClaimsLoading = false;
          this.showValidationErrors(response, claimFileId);
          return;
        }

        this.loadDocumentStep(claimFileId);
      },
      (error) => {
        this.stopValidationPolling();
        if (this.handleAuthFailure(error)) {
          this.validateClaimsLoading = false;
          return;
        }
        if (this.hasTerminalGwccCancellation(error)) {
          this.captureGwccFailureRows(error);
          this.loadDocumentStep(claimFileId);
          return;
        }
        this.validateClaimsLoading = false;
        this.showValidationErrors(error, claimFileId);
      }
    );
  }

  private isValidationInProgress(response: any): boolean {
    const body = this.parseResponseBody(response);
    const data = body && body.data && typeof body.data === 'object' ? body.data : {};
    const responseStatus = String(data.responseStatus || '').trim().toLowerCase();
    const requestDataStatus = String(data.requestDataStatus || '').trim().toLowerCase();

    return responseStatus === 'inprogress'
      || responseStatus === 'in progress'
      || requestDataStatus === 'inprogress'
      || requestDataStatus === 'in progress';
  }

  private scheduleValidationRetry(claimFileId: number): void {
    if (this.validationRetryCount >= this.validationRetryLimit) {
      this.stopValidationPolling();
      this.validateClaimsLoading = false;
      this.systemError = 'Claim preparation is taking longer than expected. Please try again.';
      return;
    }

    this.validationRetryCount++;
    this.validationRetryTimer = setTimeout(() => {
      this.validationRetryTimer = null;
      this.runValidateClaims(claimFileId);
    }, this.validationRetryDelayMs);
  }

  //This was used when Validate Claims API handled both creating draft claim #s and pinging GW for the draft claim #s. But now is not needed.
  private stopValidationPolling(): void {
    if (this.validationRetryTimer !== null) {
      clearTimeout(this.validationRetryTimer);
      this.validationRetryTimer = null;
    }
    this.validationRetryCount = 0;
  }

  // Legacy loads the documents grid from the UploadDocument page; refreshData is only the fallback.
  // Upload Document API does not actually upload any documents.
  // It will ping GW, in hopes of retrieving draft claim #s.
  // If it gets draft claim #s, or if it gets errors on some records, we will update the records in our database.
  // This newly updated information then gets returned to the frontend
  private loadDocumentStep(claimFileId: number): void {
    this.claimService.uploadDocumentClaims(this.buildUploadDocumentPayload(claimFileId)).subscribe(
      (response) => {
        const rows = this.mapRefreshDataRows(response);
        this.totalClaimsToPrepare = this.toNumberOrUndefined(response && response.totalClaims) ?? rows.length;
        if (rows.length) {
          this.applyDocumentStepRows(rows, this.resolvePositiveIdentifier(response && response.importFileID));
          return;
        }

        this.loadDocumentStepFromRefreshData(claimFileId);
      },
      (error) => {
        if (this.handleAuthFailure(error)) {
          this.validateClaimsLoading = false;
          return;
        }
        this.loadDocumentStepFromRefreshData(claimFileId);
      }
    );
  }

  private loadDocumentStepFromRefreshData(claimFileId: number): void {
    // refreshData returns {response:[]} in this environment; multiClaim/list (getReadOnlyList) is confirmed to work.
    this.claimService.getReadOnlyList(claimFileId, this.getCurrentPolicyNumber()).subscribe(
      (response) => {
        // Validation already passed; keep the in-memory rows when the server returns none, they carry claimId.
        const rows = this.mapRefreshDataRows(response);
        if (this.totalClaimsToPrepare === null) {
          this.totalClaimsToPrepare = rows.length || this.rows.length;
        }
        this.applyDocumentStepRows(rows, null);
      },
      (error) => {
        this.validateClaimsLoading = false;
        if (this.handleAuthFailure(error)) {
          return;
        }
        this.systemError = 'Unable to load claims data from server. Please try again.';
      }
    );
  }

  private applyDocumentStepRows(rows: MultiClaim2Row[], importFileId: number | null): void {
    this.validateClaimsLoading = false;

    if (rows.length) {
      this.rows = this.mergeDocumentState(rows).map((row) => this.mergePendingGwccFailure(row));
      this.currentPage = 1;
      this.clampReviewPage();
    }

    if (importFileId !== null) {
      this.lastExportFileImportId = importFileId;
    }

    this.docCurrentPage = 1;
    this.activeDocRowId = null;
    this.step = 4;
  }

  // Legacy "Save & Refresh" reloads the UploadDocument page, whose controller action re-triggers
  // GetDraftClaimNumberFromGWCC(..., "Update") for any row still missing a draft before returning data.
  // multiClaim/list (getReadOnlyList) mirrors the read-only List action and never retries draft creation -
  // multiClaim/uploadDocument (uploadDocumentClaims) is the endpoint that actually mirrors the retry.
  refreshDocumentReadiness(): void {
    const claimFileId = this.resolvePositiveIdentifier(this.getCurrentClaimFileId());
    if (claimFileId === null || this.documentsRefreshLoading) {
      return;
    }

    this.documentsRefreshLoading = true;
    this.systemError = '';
    this.claimService.uploadDocumentClaims(this.buildUploadDocumentPayload(claimFileId)).subscribe(
      (response) => {
        this.documentsRefreshLoading = false;
        this.totalClaimsToPrepare = this.toNumberOrUndefined(response && response.totalClaims) ?? this.totalClaimsToPrepare;
        const refreshedRows = this.mapRefreshDataRows(response);
        if (refreshedRows.length) {
          this.rows = this.mergeDocumentState(refreshedRows);
          this.clampDocPage();
        }
      },
      (error) => {
        this.documentsRefreshLoading = false;
        if (this.handleAuthFailure(error)) {
          return;
        }
        this.systemError = 'Unable to refresh claims right now. Please try again.';
      }
    );
  }

  // Refreshing rows from the server otherwise wipes the documents already tracked client-side this session.
  private mergeDocumentState(refreshedRows: MultiClaim2Row[]): MultiClaim2Row[] {
    if (!this.rows.length) {
      return refreshedRows;
    }

    const matchedIndexes = new Set<number>();
    const mergedRows = this.rows.map((existing) => {
      const existingClaimId = this.resolvePositiveIdentifier(existing.claimId);
      const existingTracking = String(existing.trackingNumber || '').trim().toLowerCase();
      const matchIndex = refreshedRows.findIndex((candidate, index) => {
        if (matchedIndexes.has(index)) {
          return false;
        }

        const candidateClaimId = this.resolvePositiveIdentifier(candidate.claimId);
        if (existingClaimId !== null && candidateClaimId !== null) {
          return existingClaimId === candidateClaimId;
        }

        return !!existingTracking
          && existingTracking === String(candidate.trackingNumber || '').trim().toLowerCase();
      });

      if (matchIndex < 0) {
        return existing;
      }

      matchedIndexes.add(matchIndex);
      const refreshed = refreshedRows[matchIndex];
      const refreshedReason = String(refreshed.reasonForFailure || refreshed.remarks || '').trim();
      const preservedFailureReason = this.isDraftCreationFailure(refreshed)
        ? String(existing.reasonForFailure || existing.remarks || '').trim()
        : '';
      const failureReason = refreshedReason || preservedFailureReason;
      return {
        ...existing,
        ...refreshed,
        id: existing.id,
        selected: existing.selected,
        reasonForFailure: failureReason,
        remarks: failureReason,
        documents: existing.documents.length ? existing.documents : refreshed.documents,
        documentCount: existing.documents.length || refreshed.documentCount
      };
    });

    return mergedRows.concat(refreshedRows.filter((_row, index) => !matchedIndexes.has(index)));
  }

  private clampDocPage(): void {
    if (this.docCurrentPage > this.docTotalPages) {
      this.docCurrentPage = this.docTotalPages;
    }
    if (this.docCurrentPage < 1) {
      this.docCurrentPage = 1;
    }
  }

  onDocumentsSelected(event: Event, row: MultiClaim2Row): void {
    const target = event.target as HTMLInputElement;
    const files = Array.from(target.files || []);
    this.processDroppedDocuments(files, row);
    target.value = '';
  }

  onDocumentsDropped(fileList: FileList, row: MultiClaim2Row): void {
    this.processDroppedDocuments(Array.from(fileList || []), row);
  }

  private processDroppedDocuments(files: File[], row: MultiClaim2Row): void {
    if (!files.length) {
      return;
    }

    this.systemError = '';

    const { validFiles, errorMessages } = this.validateDocumentFiles(files, row);

    if (errorMessages.length) {
      this.systemError = errorMessages.join(' ');
    }

    if (!validFiles.length) {
      return;
    }

    const claimId = this.getValidClaimId(row);
    if (claimId !== null) {
      validFiles.forEach((file) => this.uploadSubDocument(row, claimId, file));
      return;
    }

    const claimFileId = this.resolvePositiveIdentifier(row.claimFileId) ?? this.resolvePositiveIdentifier(this.getCurrentClaimFileId());
    if (claimFileId === null) {
      this.systemError = 'Claim id is missing for this row. Please refresh and try again.';
      return;
    }


    this.claimService.getReadOnlyList(claimFileId, this.getCurrentPolicyNumber()).subscribe(
      (response) => {
        const rowIndex = this.rows.indexOf(row);
        const refreshedRows = this.mapRefreshDataRows(response);
        if (!refreshedRows.length) {
          this.systemError = 'Claim details are not available yet. Please try again in a moment.';
          return;
        }

        this.rows = refreshedRows;
        this.currentPage = 1;
        this.clampReviewPage();

        const matchedRow = rowIndex > -1 ? this.rows[rowIndex] : null;
        const resolvedClaimId = matchedRow ? this.getValidClaimId(matchedRow) : null;
        if (!matchedRow || resolvedClaimId === null) {
          this.systemError = 'Claim id is missing for this row. Please refresh and try again.';
          return;
        }

        validFiles.forEach((file) => this.uploadSubDocument(matchedRow, resolvedClaimId, file));
      },
      (error) => {
        if (this.handleAuthFailure(error)) {
          return;
        }
        this.systemError = 'Claim id is missing for this row. Please refresh and try again.';
      }
    );
  }

  // Mirrors upsc-document-upload's preliminaryDocumentChecks: size/name-length/duplicate rules, adapted to this row's document list.
  private validateDocumentFiles(files: File[], row: MultiClaim2Row): { validFiles: File[]; errorMessages: string[] } {
    const validFiles: File[] = [];
    const errorMessages: string[] = [];
    const existingNames = row.documents.map((doc) => doc.name.toLowerCase());
    const availableSlots = Math.max(0, this.maxSupportingDocumentsPerClaim - this.getSupportingDocumentCount(row));

    files.forEach((file) => {
      if (validFiles.length >= availableSlots) {
        if (errorMessages.indexOf(this.documentLimitMessage) === -1) {
          errorMessages.push(this.documentLimitMessage);
        }
        return;
      }

      const sanitized = this.sanitizeFileName(file);

      if (sanitized.name.length > this.maxDocumentFileNameLength) {
        errorMessages.push(`The file ${file.name} could not be uploaded: the file name exceeds the maximum limit of 70 characters.`);
        return;
      }

      if (file.size > this.maxDocumentFileSizeBytes) {
        errorMessages.push(`The file ${file.name} could not be uploaded: file size exceeds 7MB.`);
        return;
      }

      if (existingNames.indexOf(sanitized.name.toLowerCase()) > -1) {
        errorMessages.push(`The file ${file.name} could not be uploaded: duplicate files are not allowed.`);
        return;
      }

      existingNames.push(sanitized.name.toLowerCase());
      validFiles.push(sanitized);
    });

    return { validFiles, errorMessages };
  }

  private getSupportingDocumentCount(row: MultiClaim2Row): number {
    const serverCount = Number(row.documentCount || 0);
    const existingCount = Math.max(row.documents.length, Number.isFinite(serverCount) ? serverCount : 0);
    const claimId = this.getValidClaimId(row);
    return existingCount + (claimId !== null ? this.pendingDocumentUploads.get(claimId) || 0 : 0);
  }

  // When a document has special characters in its name, replace them with spaces so it uploads cleanly (Akamai compatibility).
  private sanitizeFileName(file: File): File {
    const lastDot = file.name.lastIndexOf('.');
    const extension = lastDot > -1 ? file.name.substring(lastDot) : '';
    let nameWithoutExtension = lastDot > -1 ? file.name.substring(0, lastDot) : file.name;

    try {
      nameWithoutExtension = nameWithoutExtension.replace(/[\x00-\x08\x0E-\x1F\x7F-\uFFFF]/g, ' ');
      nameWithoutExtension = nameWithoutExtension.replace(ALL_SPECIAL_CHARACTERS, ' ');
      nameWithoutExtension = nameWithoutExtension.trim();
    } catch {
      // Keep the original name if sanitization fails for any reason.
    }

    const sanitizedName = `${nameWithoutExtension}${extension}`;

    // HEIC files often come back with an empty/unknown file.type from the browser - correct it so the backend recognizes the image.
    const mimeType = file.name.toLowerCase().endsWith('.msg') ? 'application/vnd.ms-outlook'
      : file.name.toLowerCase().endsWith('.heic') ? 'image/heic'
      : file.type;

    if (sanitizedName === file.name && mimeType === file.type) {
      return file;
    }

    return new File([file], sanitizedName, { type: mimeType });
  }

  removeDocument(row: MultiClaim2Row, index: number): void {
    const target = row.documents[index];
    if (!target) {
      return;
    }

    this.systemError = '';
    if (!target.publicID) {
      row.documents.splice(index, 1);
      row.documentCount = row.documents.length;
      return;
    }

    const publicId = String(target.publicID);
    this.setDocumentDeleteState(publicId, true);
    this.claimService.deleteSubDocument(publicId).subscribe(
      (response) => {
        this.setDocumentDeleteState(publicId, false);

        const message = response && response.message ? String(response.message).toLowerCase() : '';
        if (message.indexOf('deleted') === -1 && message.indexOf('success') === -1) {
          this.systemError = response && response.message ? response.message : 'Unable to delete document right now. Please try again.';
          return;
        }

        const remaining = this.mapSubDocumentRows(response && response.data ? response.data : []);
        row.documents = remaining;
        row.documentCount = remaining.length;

        if (remaining.length && remaining[0].publicID && !row.claimFileId) {
          const activeClaimFileId = this.getCurrentClaimFileId();
          if (activeClaimFileId !== null) {
            row.claimFileId = activeClaimFileId;
          }
        }
      },
      () => {
        this.setDocumentDeleteState(publicId, false);
        this.systemError = 'Unable to delete document right now. Please try again.';
      }
    );
  }

  isDocumentUploading(row: MultiClaim2Row): boolean {
    const claimId = this.getValidClaimId(row);
    if (claimId === null) {
      return false;
    }

    return this.uploadingClaimIds.indexOf(claimId) > -1;
  }

  isDocumentDeleting(doc: MultiClaim2Document): boolean {
    const publicId = doc && doc.publicID ? String(doc.publicID) : '';
    if (!publicId) {
      return false;
    }

    return this.deletingDocumentIds.indexOf(publicId) > -1;
  }

  continueToSummary(): void {
    this.systemError = '';

    if (!this.documentsAllPrepared) {
      this.systemError = 'Please wait for all claims to finish preparing before continuing.';
      return;
    }

    const claimFileId = this.resolvePositiveIdentifier(this.getCurrentClaimFileId());

    if (claimFileId === null) {
      this.step = 5;
      return;
    }

    // multiClaim/list (getReadOnlyList) doesn't return documentCount, unlike uploadDocument/submittedList/refreshData -
    // fetch from uploadDocumentClaims first so the summary's "Number of claims with documents" isn't blanked to 0.
    this.claimService.uploadDocumentClaims(this.buildUploadDocumentPayload(claimFileId)).subscribe(
      (response) => {
        const refreshedRows = this.mapRefreshDataRows(response);
        if (refreshedRows.length) {
          this.rows = this.mergeDocumentState(refreshedRows);
        }
        this.loadSummaryPage(claimFileId);
      },
      (error) => {
        if (this.handleAuthFailure(error)) {
          return;
        }
        this.loadSummaryFromReadOnlyList(claimFileId);
      }
    );
  }

  private loadSummaryFromReadOnlyList(claimFileId: number): void {
    this.claimService.getReadOnlyList(claimFileId, this.getCurrentPolicyNumber()).subscribe(
      (response) => {
        const refreshedRows = this.mapRefreshDataRows(response);
        if (refreshedRows.length) {
          this.rows = this.mergeDocumentState(refreshedRows);
        }
        this.loadSummaryPage(claimFileId);
      },
      (error) => {
        if (this.handleAuthFailure(error)) {
          return;
        }
        this.systemError = 'Unable to refresh claims right now. Showing your current data.';
        this.loadSummaryPage(claimFileId);
      }
    );
  }

  private loadSummaryPage(claimFileId: number): void {
    this.claimService.getSummaryPage(claimFileId, this.getCurrentPolicyNumber()).subscribe(
      () => this.step = 5,
      () => this.step = 5
    );
  }

  exportToExcel(): void {
    this.exportError = '';
    if (!this.rows.length) {
      this.exportError = 'No rows available to export.';
      return;
    }

    // Header names must match the backend upload template's expected columns (no spaces) - Title Case
    // headers with spaces (the old export format) fail the server's "columns are missing" check on re-upload.
    const exportRows = this.rows.map((row) => ({
      TrackingNumber: row.trackingNumber,
      TransportationCarrier: row.carrier,
      NatureOfLoss: row.natureOfLoss,
      MerchandiseDescription: row.merchandiseDescription,
      ShipDate: row.shipDate,
      ReplacementTrackingNumber: row.replacementTrackingNumber,
      MerchandiseAmount: row.merchandiseAmount,
      ShippingAmount: row.shippingAmount,
      TotalAmount: row.totalAmount,
      CreditMemo: row.creditMemo,
      AdditionalComments: row.additionalComments,
      Ref1: row.ref1
    }));

    const worksheet = XLSX.utils.json_to_sheet(exportRows);
    const workbook = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(workbook, worksheet, 'Claims');

    const data = XLSX.write(workbook, { bookType: 'xlsx', type: 'array' });
    const blob = new Blob([data], { type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' });
    const fileName = `${this.currentSubmissionNumber || this.getCurrentClaimFileId() || 'MultiClaim2'}-Export.xlsx`;
    FileSaver.saveAs(blob, fileName);
  }

  cancelExportToExcel(): void {
    this.exportError = '';
    if (this.cancelExportLoading) {
      return;
    }

    const claimFileId = this.getCurrentClaimFileId();
    if (claimFileId === null || claimFileId === undefined || claimFileId === '') {
      // If there is no server-side id yet, still allow users to cancel local progress and continue.
      this.resetUploadFlow('Upload cancelled.');
      return;
    }

    this.cancelExportLoading = true;
    this.claimService.cancelUpload(claimFileId, this.getCurrentPolicyNumber()).subscribe(
      (response) => {
        this.cancelExportLoading = false;
        this.lastExportFileImportId = null;
        const msg = response && response.message ? String(response.message) : 'Cancelled successfully';
        this.rows = this.rows.map((row) => ({ ...row, selected: false }));
        this.loadRecentUploads(true);
        this.resetUploadFlow(msg);
      },
      () => {
        this.cancelExportLoading = false;
        this.exportError = 'Unable to cancel upload right now. Please try again.';
      }
    );
  }

  submitExportToExcel(): void {
    this.exportError = '';
    if (this.submitExportLoading) {
      return;
    }

    const currentClaimFileId = this.getCurrentClaimFileId();
    const targetUpload = this.findRecentUpload(currentClaimFileId);
    const targetClaimFileId = currentClaimFileId !== null && currentClaimFileId !== undefined && currentClaimFileId !== ''
      ? currentClaimFileId
      : (targetUpload && targetUpload.claimFileId !== undefined && targetUpload.claimFileId !== null && targetUpload.claimFileId !== ''
        ? targetUpload.claimFileId
        : null);

    if (!targetClaimFileId) {
      this.exportError = 'No file available to export.';
      return;
    }

    const fileState = targetUpload && targetUpload.importStatus ? targetUpload.importStatus.toUpperCase() : '';
    
    // If state is ACCEPTED, call submitExportExcel API
    if (fileState === 'ACCEPTED') {
      this.submitExportLoading = true;
      this.claimService.submitExportExcel(targetClaimFileId).subscribe(
        (response: HttpResponse<Blob>) => {
          this.submitExportLoading = false;
          this.saveExportBlob(response, targetClaimFileId);
          this.exportError = 'Export submitted successfully.';
          this.lastExportFileImportId = targetClaimFileId;
          this.refreshRowsForClaimFile(targetClaimFileId);
        },
        () => {
          this.submitExportLoading = false;
          this.exportError = 'Unable to submit export right now. Please try again.';
        }
      );
    }
    // If state is CANCELED or FAILED, call cancelExportExcel API
    else if (fileState === 'CANCELED' || fileState === 'FAILED') {
      this.cancelExportLoading = true;
      this.claimService.cancelExportExcel(targetClaimFileId).subscribe(
        (response: HttpResponse<Blob>) => {
          this.cancelExportLoading = false;
          this.saveExportBlob(response, targetClaimFileId);
          this.exportError = 'Export cancellation file downloaded successfully.';
          this.loadRecentUploads(true);
        },
        () => {
          this.cancelExportLoading = false;
          this.exportError = 'Unable to cancel upload right now. Please try again.';
        }
      );
    }
    // For PENDING or other states, do client-side export
    else {
      this.exportToExcel();
    }
  }

  submitClaims(): void {
    if (!this.acknowledge || this.submitClaimsLoading) {
      return;
    }

    this.systemError = '';
    this.submitError = '';
    const claimFileId = this.resolvePositiveIdentifier(this.getCurrentClaimFileId());
    if (claimFileId === null) {
      this.submitError = 'No upload request id is available for submission.';
      return;
    }

    this.submitClaimsLoading = true;
    this.claimService.submitClaimsGwcc(this.buildSubmitClaimPayload(claimFileId)).subscribe(
      (submitResponse) => {
        this.submitClaimsLoading = false;
        const submitMessage = submitResponse && submitResponse.message ? String(submitResponse.message).toLowerCase() : '';
        const submitted = submitMessage === 'submitted' || submitMessage === 'success';

        if (!submitted) {
          this.submitError = submitMessage === 'failed' || submitMessage === 'error in t4 api'
            ? 'An unknown error occurred. Please contact the UPS Capital Customer Support Center.'
            : ((submitResponse && submitResponse.message) ? submitResponse.message : 'An unknown error occurred. Please contact the UPS Capital Customer Support Center.');
          return;
        }

        // Legacy sets the raw ImportStatus to "Finalized" on the same row (SaveClaimErrorInformation), which the Recent Uploads grid displays as "Accepted".
        this.updateRecentUploadStatus(claimFileId, 'Finalized');
        this.currentSubmissionNumber = String(this.extractClaimFileIdFromResponse(submitResponse) || claimFileId);
        this.claimService.getSuccessPage(claimFileId).subscribe(
          () => this.step = 6,
          () => this.step = 6
        );
      },
      (error) => {
        this.submitClaimsLoading = false;
        if (this.handleAuthFailure(error)) {
          return;
        }
        this.submitError = 'An unknown error occurred. Please contact the UPS Capital Customer Support Center.';
      }
    );
  }

  private showValidationErrors(response: any, claimFileId?: number): void {
    const body = this.parseResponseBody(response);
    const rawMessage = body && body.message ? String(body.message).trim() : '';
    const message = /^error in gw\/ms call\.?$/i.test(rawMessage)
      ? 'System error. Please try again.'
      : rawMessage;
    const responseRows = this.mapRefreshDataRows(body && body.data !== undefined ? { data: body.data } : body);
    const rowsWithErrors = this.rows.length
      ? this.rows.map((existingRow, index) => {
        const errorRow = this.findValidationRowFor(existingRow, responseRows, index);
        return errorRow ? this.mergeValidationRow(existingRow, errorRow) : this.mergeValidationRow(existingRow, null);
      })
      : responseRows;

    if (rowsWithErrors.length) {
      this.applyValidationErrorRows(rowsWithErrors, message);
      return;
    }

    // Body shape was unusable; reload the rows so the flagged records are still highlighted.
    if (claimFileId) {
      this.claimService.getReadOnlyList(claimFileId, this.getCurrentPolicyNumber()).subscribe(
        (refreshResponse) => {
          const refreshedRows = this.mapRefreshDataRows(refreshResponse);
          if (refreshedRows.length) {
            this.applyValidationErrorRows(refreshedRows, message);
            return;
          }
          this.systemError = message || 'Unable to validate claims right now. Please try again.';
        },
        () => {
          this.systemError = message || 'Unable to validate claims right now. Please try again.';
        }
      );
      return;
    }

    this.systemError = message || 'Unable to validate claims right now. Please try again.';
  }

  // A terminal GWCC rejection is already persisted as a failed/cancelled claim; legacy still opens UploadDocument for the remaining batch.
  private hasTerminalGwccCancellation(response: any): boolean {
    const body = this.parseResponseBody(response);
    const responseRows = this.mapRefreshDataRows(body && body.data !== undefined ? { data: body.data } : body);
    const responseMessage = String(body && body.message || '');

    return responseRows.some((row) => this.isGwccCancellationFailure(row))
      || this.isPolicyPeriodCancellationMessage(responseMessage)
      || responseRows.some((row) => this.isPolicyPeriodCancellationMessage(`${row.remarks || ''} ${row.reasonForFailure || ''}`));
  }

  // The validation endpoint returns this terminal GWCC outcome before its follow-up document endpoint exposes GWCCClaimStatus = Failed.
  private isPolicyPeriodCancellationMessage(message: string): boolean {
    const normalized = String(message || '').toLowerCase();
    return (normalized.indexOf('loss date') > -1 || normalized.indexOf('ship date') > -1)
      && normalized.indexOf('policy period') > -1;
  }

  isGwccCancellationFailure(row: MultiClaim2Row): boolean {
    const status = String(row && row.gwccClaimStatus || '').trim().toLowerCase();
    return status === 'failed' || status === 'cancelled' || status === 'canceled';
  }

  private captureGwccFailureRows(response: any): void {
    const body = this.parseResponseBody(response);
    const responseRows = this.mapRefreshDataRows(body && body.data !== undefined ? { data: body.data } : body);
    const message = String(body && body.message || '').trim();

    this.pendingGwccFailureRows = responseRows.map((row) => ({
      ...row,
      reasonForFailure: row.reasonForFailure || row.remarks || message,
      remarks: row.remarks || row.reasonForFailure || message
    }));

    if (!this.pendingGwccFailureRows.length && message) {
      this.pendingGwccFailureRows = [{
        ...this.rows[0],
        reasonForFailure: message,
        remarks: message
      }];
    }
  }

  private mergePendingGwccFailure(row: MultiClaim2Row): MultiClaim2Row {
    if (!this.isGwccCancellationFailure(row)) {
      return row;
    }

    const failedRow = this.findValidationRowFor(row, this.pendingGwccFailureRows, 0);
    const reason = failedRow && (failedRow.reasonForFailure || failedRow.remarks);
    return reason ? { ...row, reasonForFailure: reason, remarks: reason } : row;
  }

  // GWCC can return a subset of rows in a different order, so pair on claimId/tracking number before falling back to position.
  private findValidationRowFor(existingRow: MultiClaim2Row, responseRows: MultiClaim2Row[], index: number): MultiClaim2Row | null {
    const claimId = this.resolvePositiveIdentifier(existingRow.claimId);
    if (claimId !== null) {
      const byClaimId = responseRows.find((candidate) => this.resolvePositiveIdentifier(candidate.claimId) === claimId);
      if (byClaimId) {
        return byClaimId;
      }
    }

    const tracking = String(existingRow.trackingNumber || '').trim().toLowerCase();
    if (tracking) {
      const byTracking = responseRows.find((candidate) => String(candidate.trackingNumber || '').trim().toLowerCase() === tracking);
      if (byTracking) {
        return byTracking;
      }
    }

    const hasIdentifiers = responseRows.some((candidate) => this.resolvePositiveIdentifier(candidate.claimId) !== null);
    return hasIdentifiers ? null : (responseRows[index] || null);
  }

  // The validation response echoes stale GWCC data, so only its error/status metadata is applied to the row.
  private mergeValidationRow(existingRow: MultiClaim2Row, errorRow: MultiClaim2Row | null): MultiClaim2Row {
    const merged: MultiClaim2Row = { ...existingRow };

    if (!errorRow) {
      merged.remarks = '';
      merged.reasonForFailure = '';
      merged.isValid = 1;
      if (this.isGwccFailureStatus(merged.gwccClaimStatus)) {
        merged.gwccClaimStatus = '';
      }
      return merged;
    }

    merged.remarks = errorRow.remarks || errorRow.reasonForFailure || '';
    merged.reasonForFailure = errorRow.reasonForFailure || '';
    merged.isValid = errorRow.isValid;

    if (errorRow.gwccClaimStatus) {
      merged.gwccClaimStatus = errorRow.gwccClaimStatus;
    }
    if (errorRow.draftClaimNo) {
      merged.draftClaimNo = errorRow.draftClaimNo;
    }
    if (this.resolvePositiveIdentifier(errorRow.claimId) !== null) {
      merged.claimId = errorRow.claimId;
    }
    if (this.resolvePositiveIdentifier(errorRow.claimFileId) !== null) {
      merged.claimFileId = errorRow.claimFileId;
    }

    return merged;
  }

  private applyValidationErrorRows(rows: MultiClaim2Row[], message = ''): void {
    this.cancelEditRow();
    this.rows = rows;
    this.currentPage = 1;
    this.step = 3;

    const hasRowErrors = this.rows.some((row) => this.isReviewRowInvalid(row));
    this.reviewFilter = hasRowErrors ? 'errors' : 'all';    this.clampReviewPage();

    // A submission-level failure (e.g. loss date outside the policy period) flags no row, so the row-level banner would be empty.
    if (hasRowErrors) {
      this.systemError = '';
      // Top banner ("N Errors Found...") already communicates this; avoid a duplicate message here.
      this.reviewActionError = '';
      return;
    }

    this.reviewActionError = '';
    this.systemError = message || 'Unable to validate claims right now. Please try again.';
  }

  // Error bodies reach us as the parsed object, a JSON string, or wrapped in a `text` property.
  private parseResponseBody(source: any): any {
    const error = this.unwrapError(source);
    let body = error && error.error !== undefined ? error.error : error;

    if (body && typeof body === 'object' && typeof body.text === 'string') {
      body = body.text;
    }

    if (typeof body === 'string') {
      try {
        return JSON.parse(body);
      } catch {
        return { message: body };
      }
    }

    return body;
  }

  // MSAL's interceptor uses the rxjs 7 throwError(() => err) signature, so errors can arrive as a factory.
  private unwrapError(error: any): any {
    let current = error;
    let depth = 0;

    while (typeof current === 'function' && depth < 3) {
      try {
        current = current();
      } catch {
        return null;
      }
      depth++;
    }

    return current;
  }

  saveForLater(): void {
    if (this.validateClaimsLoading) {
      return;
    }

    this.addRecentUpload('Pending');
    this.resetFlow();
  }

  backToLanding(): void {
    this.resetFlow();
  }

  // Back/Cancel are only meaningful before GWCC starts drafting claim numbers (which happens on Continue
  // from Review Data). Once step 4 (Upload Supporting Documents) is reached, neither is offered again.
  backToUploadStep(): void {
    if (this.validateClaimsLoading) {
      return;
    }

    this.systemError = '';
    this.step = 2;
  }

  backToReviewFromSummary(): void {
    this.systemError = '';
    this.step = 4;
  }

  openCancelUploadModal(): void {
    if (this.validateClaimsLoading) {
      return;
    }

    if (this.getCurrentClaimFileId() === null || this.getCurrentClaimFileId() === undefined || this.getCurrentClaimFileId() === '') {
      this.resetFlow();
      return;
    }

    this.showCancelUploadModal = true;
  }

  closeCancelUploadModal(): void {
    this.showCancelUploadModal = false;
  }

  confirmCancelUpload(): void {
    if (this.validateClaimsLoading) {
      return;
    }

    this.showCancelUploadModal = false;

    const claimFileId = this.getCurrentClaimFileId();
    if (claimFileId === null || claimFileId === undefined || claimFileId === '' || this.cancelExportLoading) {
      this.resetFlow();
      return;
    }

    this.cancelExportLoading = true;
    this.claimService.cancelUpload(claimFileId, this.getCurrentPolicyNumber()).subscribe(
      () => {
        this.cancelExportLoading = false;
        this.lastExportFileImportId = null;
        this.resetFlow();
      },
      (error) => {
        this.cancelExportLoading = false;
        if (this.handleAuthFailure(error)) {
          return;
        }
        this.exportError = 'Unable to cancel upload right now. Please try again.';
      }
    );
  }

  private setSelectedFile(file: File): void {
    this.selectedFile = file;
    this.parseError = '';
  }

  private clearEmptySubmissionState(): void {
    if (this.rows.length > 0) {
      return;
    }

    this.selectedFile = null;
    this.lastExportFileImportId = null;
    this.currentSubmissionNumber = '';
    this.reviewFilter = 'all';
    this.currentPage = 1;
  }

  private resetFlow(): void {
    this.stopValidationPolling();
    // Legacy Cancel/Save & Continue Later/Home all redirect to MultiClaim/Home (step 2), not the pre-DPAT welcome page.
    this.step = 2;
    this.isReadOnlyView = false;
    this.showContinueConfirmModal = false;
    this.showCancelUploadModal = false;
    this.selectedFile = null;
    this.rows = [];
    this.parseError = '';
    this.systemError = '';
    this.acknowledge = false;
    this.currentSubmissionNumber = '';
    this.reviewFilter = 'all';
    this.pageSize = 10;
    this.currentPage = 1;
    this.lastExportFileImportId = null;
    this.uploadingClaimIds = [];
    this.deletingDocumentIds = [];
    this.totalClaimsToPrepare = null;
    this.docCurrentPage = 1;
    this.activeDocRowId = null;
    this.editingRowId = null;
    this.editingDraft = null;
    this.editingRowLoading = false;
    this.validateClaimsLoading = false;
    this.submitClaimsLoading = false;
    this.loadRecentUploads(false);
  }
  private findRecentUpload(claimFileId?: number | string | null): MultiClaim2RecentUpload | null {
    if (!this.recentUploads || !this.recentUploads.length) {
      return null;
    }

    if (claimFileId !== null && claimFileId !== undefined && claimFileId !== '') {
      const normalizedId = String(claimFileId);
      const match = this.recentUploads.find((upload) => String(upload.claimFileId) === normalizedId);
      if (match) {
        return match;
      }
    }

    return this.recentUploads[0];
  }


  private syncFlowLayoutMode(): void {
    if (this.step >= 1) {
      this.renderer.addClass(this.document.body, 'mc2-fullscreen-flow');
      return;
    }

    this.renderer.removeClass(this.document.body, 'mc2-fullscreen-flow');
  }

  private clampReviewPage(): void {
    if (this.currentPage > this.reviewTotalPages) {
      this.currentPage = this.reviewTotalPages;
    }
    if (this.currentPage < 1) {
      this.currentPage = 1;
    }
  }

  private prefillContactDetails(): void {
    const profile: any = this.userService.getUserInfo() || {};

    this.contactFirstName = profile.firstName || '';
    this.contactLastName = profile.lastName || '';
    this.contactName = `${this.contactFirstName} ${this.contactLastName}`.trim();
    this.contactEmail = profile.emailAddress || '';
    this.contactPhone = profile.workNumber || profile.phoneNumber || '';

    this.recipientName = this.contactName;
    this.recipientEmail = this.contactEmail;
    this.recipientPhone = this.contactPhone;

    const addressLine1 = profile.addressLine1 || profile.streetAddress1 || profile.address1 || profile.address || '';
    const addressLine2 = profile.addressLine2 || profile.streetAddress2 || profile.address2 || '';
    const city = profile.city || profile.town || '';
    const state = profile.state || profile.stateProv || profile.province || '';
    const zip = profile.zipCode || profile.postalCode || profile.zip || '';

    this.recipientAddressLine1 = String(addressLine1).trim();
    this.recipientAddressLine2 = [addressLine2, city, state, zip].filter(Boolean).join(' ').trim();
  }

  private loadRecipientDetails(): void {
    const payeeId = this.getCurrentPayeeId();
    const policyNumber = this.getCurrentPolicyNumber();

    if (!payeeId || !policyNumber) {
      return;
    }

    const request = {
      payeeID: payeeId,
      policyNumber
    };

    this.recipientLoading = true;
    this.claimService.getPayeeDetails(request).subscribe(
      (response) => {
        this.recipientLoading = false;
        const hasExplicitFailure = response && response.success && String(response.success).toLowerCase() === 'false';
        const payee = response && response.data ? response.data : null;

        if (hasExplicitFailure || !payee) {
          return;
        }

        this.recipientName = String(payee.payeeName || payee.name || this.recipientName || '').trim();
        if (this.recipientName) {
          sessionStorage.setItem(this.dpatPayeeNameKey, this.recipientName);
        }
        this.recipientEmail = String(payee.email || payee.emailAddress || this.recipientEmail || '').trim();
        this.recipientPhone = String(payee.phoneNumber || payee.contactPhone || payee.phone || this.recipientPhone || '').trim();

        const serializedAddress = String(payee.address || payee.payeeAddress || '').trim();
        const serializedParts = serializedAddress
          ? serializedAddress.split(',').map((part: string) => String(part || '').trim()).filter((part: string) => !!part)
          : [];

        const line1 = String(payee.addressLine1 || payee.address1 || serializedParts[0] || '').trim();
        const line2Parts = [
          String(payee.addressLine2 || payee.address2 || '').trim(),
          String(payee.city || '').trim(),
          String(payee.state || payee.stateCode || payee.province || '').trim(),
          String(payee.zip || payee.zipCode || payee.postalCode || '').trim()
        ].filter((part) => !!part);

        const line2 = line2Parts.length ? line2Parts.join(' ').trim() : String(serializedParts[1] || '').trim();

        this.recipientAddressLine1 = line1 || this.recipientAddressLine1;
        this.recipientAddressLine2 = line2 || this.recipientAddressLine2;
      },
      () => {
        // Keep profile-based fallback values when payee details are unavailable.
        this.recipientLoading = false;
      }
    );
  }

  private getCurrentPayeeId(): string {
    const payeeFromUrl = this.getQueryParam('payeeID') || this.getQueryParam('payeeId');
    const storedPayeeId = sessionStorage.getItem(this.dpatPayeeKey) || '';
    const selectedPayee: any = this.payeeService.getClaimsPayeeInfo() || {};
    const recentPayee: any = this.payeeService.recentPayeeSubmission || {};
    const profile: any = this.userService.getUserInfo() || {};

    const payeeId = payeeFromUrl
      || storedPayeeId
      || selectedPayee.payeeID
      || selectedPayee.payeeId
      || recentPayee.payeeID
      || profile.payeeID
      || profile.payeeId
      || '';

    return String(payeeId).trim();
  }

  // DPAT appends payeeID/status to the callback URL; persist them before the query string is cleared.
  private captureDpatContext(): void {
    const payeeId = this.getQueryParam('payeeID') || this.getQueryParam('payeeId');
    const status = this.getQueryParam('status').toLowerCase();

    if (payeeId && payeeId !== 'null' && (!status || status === 'success')) {
      sessionStorage.setItem(this.dpatPayeeKey, payeeId);
    }
  }

  // DPAT can hand back a malformed query string ("?returnFromDpat=true?payeeID=1&status=success").
  private getQueryParam(name: string): string {
    const search = (window.location.search || '').replace(/^\?/, '').replace(/\?/g, '&');
    const value = new URLSearchParams(search).get(name) || '';
    return value.trim();
  }

  private buildUserDataContext(): IValidateClaimsRequest {
    const profile: any = this.userService.getUserInfo() || {};
    const profileName = [profile.firstName, profile.lastName].filter(Boolean).join(' ').trim();
    const storedPayeeName = sessionStorage.getItem(this.dpatPayeeNameKey) || '';
    const channelPartner = this.getChannelPartnerInfo();

    return {
      PolicyNo: this.getCurrentPolicyNumber(),
      PayeeID: this.getCurrentPayeeId(),
      UserName: profile.userName || profileName || profile.userID || profile.emailAddress || '',
      PayeeName: this.recipientName || storedPayeeName || this.contactName || profileName || '',
      GroupCode: this.getGroupCode(),
      ClaimSource: channelPartner.groupCode ? 'CCP4CP-Master' : 'ONL'
    };
  }

  // ONL login stores groupCode on userDetails; channel partner flows carry it in happyReturnsInfo.
  // Legacy leaves it blank for direct ONL sessions, so no email fallback here.
  private getGroupCode(): string {
    const profile: any = this.userService.getUserInfo() || {};
    const fromProfile = profile.groupCode || (profile.userDetails && profile.userDetails.groupCode) || '';
    return String(fromProfile || this.getChannelPartnerInfo().groupCode || '').trim();
  }

  // Legacy sourced groupCode/claimSource from the ONL session; in the portal they come from the channel partner context.
  private getChannelPartnerInfo(): { groupCode: string; partnerName: string } {
    try {
      const info: any = this.happyReturnsService.getHappyReturnsInfo();
      if (info && info.groupCode) {
        return { groupCode: String(info.groupCode).trim(), partnerName: String(info.partnerName || '').trim() };
      }
    } catch {
      // No channel partner context available.
    }

    return { groupCode: '', partnerName: '' };
  }

  private getCurrentPolicyNumber(): string {
    const policy: any = this.loginService.getPolicyDetails() || {};
    const profile: any = this.userService.getUserInfo() || {};
    return policy.policyNumber || profile.policyNumber || '';
  }

  // The API validates the persisted import by claimFileId; sending all rows again can exceed request limits for a 500-row batch.
  private buildValidateClaimsPayload(): IValidateClaimsRequest {
    return this.buildUserDataContext();
  }

  private buildUploadDocumentPayload(claimFileId: number | string): IUploadDocumentRequest {
    return {
      ClaimFileId: Number(claimFileId) || 0,
      PolicyNo: this.getCurrentPolicyNumber(),
      PayeeId: this.getCurrentPayeeId()
    };
  }

  private buildSubmitClaimPayload(claimFileId: number | string): ISubmitClaimRequest {
    const profile: any = this.userService.getUserInfo() || {};
    const firstName = this.contactFirstName || profile.firstName || '';
    const lastName = this.contactLastName || profile.lastName || '';
    const contactNumber = this.contactPhone || profile.workNumber || profile.phoneNumber || this.recipientPhone || '';
    const emailAddress = this.contactEmail || profile.emailAddress || this.recipientEmail || '';

    return {
      claimFileId: Number(claimFileId) || 0,
      firstName,
      lastName,
      contactNumber,
      emailAddress,
      groupCode: this.getGroupCode()
    };
  }

  private buildUpdateClaimPayload(row: MultiClaim2Row, claimId: number, claimFileId: number): any {
    return {
      claimID: claimId,
      claimFileId,
      transportationCarrier: row.carrier,
      trackingNumber: row.trackingNumber,
      natureofLoss: row.natureOfLoss,
      merchandiseDescription: row.merchandiseDescription,
      shipDate: this.toApiDate(row.shipDate),
      merchandiseAmount: String(row.merchandiseAmount ?? ''),
      shippingAmount: String(row.shippingAmount ?? ''),
      totalAmount: String(row.totalAmount ?? ''),
      replacementTrackingNumber: row.replacementTrackingNumber || '',
      creditMemo: row.creditMemo || '',
      ref1: row.ref1,
      additionalComments: row.additionalComments,
      policyNumber: this.getCurrentPolicyNumber()
    };
  }

  // updateClaimDetails expects yyyy-MM-dd, while rows carry the display format from the API/file.
  private toApiDate(value: string): string {
    const raw = String(value || '').trim();
    if (!raw || /^\d{4}-\d{2}-\d{2}$/.test(raw)) {
      return raw;
    }

    const parsed = new Date(raw);
    if (Number.isNaN(parsed.getTime())) {
      return raw;
    }

    const month = `${parsed.getMonth() + 1}`.padStart(2, '0');
    const day = `${parsed.getDate()}`.padStart(2, '0');
    return `${parsed.getFullYear()}-${month}-${day}`;
  }

  // yyyy-MM-dd for the Ship Date input's [max], so the native picker greys out/blocks future dates.
  get todayApiDate(): string {
    return this.toApiDate(new Date().toString());
  }

  private uploadSubDocument(row: MultiClaim2Row, claimId: number, file: File): void {
    // Recheck at dispatch time as batches and claim-id resolution can overlap.
    if (this.getSupportingDocumentCount(row) >= this.maxSupportingDocumentsPerClaim) {
      this.systemError = this.documentLimitMessage;
      return;
    }

    this.setDocumentUploadState(claimId, true);

    this.claimService.uploadSubDocument(claimId, file).subscribe(
      (response) => {
        this.setDocumentUploadState(claimId, false);

        const latest = Array.isArray(response) && response.length ? response[response.length - 1] : null;
        // A missing/blank status is NOT a success - only an explicit 'accepted' from the server counts (e.g. unsupported types like .heic come back without one).
        const status = latest && latest.status ? String(latest.status).toLowerCase() : '';
        if (status !== 'accepted') {
          this.systemError = `The file ${file.name} could not be uploaded: unsupported or invalid file.`;
          return;
        }

        const uploadedDocs = this.mapSubDocumentRows(response);
        if (!uploadedDocs.length) {
          this.systemError = `The file ${file.name} could not be uploaded: unsupported or invalid file.`;
          return;
        }

        row.documents = uploadedDocs;
        row.documentCount = Array.isArray(response) ? response.length : row.documents.length;

        if (latest && latest.claimFileId !== undefined && latest.claimFileId !== null) {
          row.claimFileId = latest.claimFileId;
          this.lastExportFileImportId = latest.claimFileId;
        }
      },
      () => {
        this.setDocumentUploadState(claimId, false);
        this.systemError = `Unable to upload ${file.name}. Please try again.`;
      }
    );
  }

  private setDocumentUploadState(claimId: number, isUploading: boolean): void {
    const pendingCount = Math.max(0, (this.pendingDocumentUploads.get(claimId) || 0) + (isUploading ? 1 : -1));
    if (pendingCount > 0) {
      this.pendingDocumentUploads.set(claimId, pendingCount);
    } else {
      this.pendingDocumentUploads.delete(claimId);
    }

    const current = this.uploadingClaimIds.indexOf(claimId);
    if (isUploading) {
      if (current === -1) {
        this.uploadingClaimIds = [...this.uploadingClaimIds, claimId];
      }
      return;
    }

    if (current > -1 && pendingCount === 0) {
      this.uploadingClaimIds = this.uploadingClaimIds.filter((id) => id !== claimId);
    }
  }

  private setDocumentDeleteState(publicId: string, isDeleting: boolean): void {
    const current = this.deletingDocumentIds.indexOf(publicId);
    if (isDeleting) {
      if (current === -1) {
        this.deletingDocumentIds = [...this.deletingDocumentIds, publicId];
      }
      return;
    }

    if (current > -1) {
      this.deletingDocumentIds = this.deletingDocumentIds.filter((id) => id !== publicId);
    }
  }

  private mapSubDocumentRows(items: any[]): MultiClaim2Document[] {
    if (!Array.isArray(items)) {
      return [];
    }

    return items
      .map((item: any) => ({
        name: String(this.getFieldValue(item, ['documentName', 'DocumentName', 'name', 'Name']) || '').trim(),
        publicID: this.getFieldValue(item, ['publicID', 'PublicID', 'publicId', 'PublicId']) !== undefined
          ? String(this.getFieldValue(item, ['publicID', 'PublicID', 'publicId', 'PublicId']))
          : undefined,
        status: this.getFieldValue(item, ['status', 'Status']) !== undefined
          ? String(this.getFieldValue(item, ['status', 'Status']))
          : undefined,
        uploadedDate: this.getFieldValue(item, ['uploadedDate', 'UploadedDate']) !== undefined
          ? String(this.getFieldValue(item, ['uploadedDate', 'UploadedDate']))
          : undefined
      }))
      // Incomplete server records can have a status but no persisted file metadata; do not render
      // them as uploaded documents. Accepted documents always have both a name and upload date.
      .filter((document) => !!document.name && !!document.uploadedDate);
  }

  private getTemplateFileName(contentDisposition: string | null): string {
    if (!contentDisposition) {
      return 'MultiClaimTemplate.xlsx';
    }

    const utf8Match = /filename\*=UTF-8''([^;]+)/i.exec(contentDisposition);
    if (utf8Match && utf8Match[1]) {
      return decodeURIComponent(utf8Match[1]);
    }

    const plainMatch = /filename="?([^";]+)"?/i.exec(contentDisposition);
    if (plainMatch && plainMatch[1]) {
      return plainMatch[1];
    }

    return 'MultiClaimTemplate.xlsx';
  }

  private handleDpatReturn(): void {
    const isAwaitingVerification = sessionStorage.getItem(this.dpatReturnKey) === 'true';
    const status = this.getQueryParam('status').toLowerCase();
    const hasDpatParams = !!status || !!this.getQueryParam('payeeID');

    if (!isAwaitingVerification && !hasDpatParams) {
      return;
    }

    this.step = (status === 'back' || status === 'cancel') ? 0 : 1;
    sessionStorage.removeItem(this.dpatReturnKey);

    if (hasDpatParams) {
      window.history.replaceState({}, document.title, window.location.pathname);
    }
  }

  private addRecentUpload(defaultStatus = 'Accepted'): void {
    if (!this.selectedFile) {
      return;
    }

    const uploadProfile: any = this.userService.getUserInfo() || {};
    const policy: any = this.loginService.getPolicyDetails() || {};
    const next: MultiClaim2RecentUpload = {
      claimFileId: this.resolvePositiveIdentifier(this.getCurrentClaimFileId()) || '',
      fileName: this.selectedFile.name,
      blobGuid: '',
      error: defaultStatus === 'Accepted' ? '' : defaultStatus,
      importStatus: defaultStatus,
      importedOn: new Date().toISOString(),
      importedBy: uploadProfile.emailAddress || 'Current User',
      policyNo: policy.policyNumber || '',
      gwccStatus: '',
      groupCode: this.getGroupCode()
    };

    this.recentUploads = [next, ...this.recentUploads].slice(0, 25);
    localStorage.setItem(this.recentStorageKey, JSON.stringify(this.recentUploads));
  }

  private updateRecentUploadStatus(claimFileId: number | string, rawStatus: string): void {
    const normalizedId = String(claimFileId);
    const existing = this.recentUploads.find((upload) => String(upload.claimFileId) === normalizedId);

    if (!existing) {
      this.addRecentUpload(rawStatus);
      return;
    }

    existing.importStatus = rawStatus;
    existing.error = '';
    this.recentUploads = [...this.recentUploads];
    localStorage.setItem(this.recentStorageKey, JSON.stringify(this.recentUploads));
  }

  private loadRecentUploads(showErrorOnFailure = false): void {
    this.historyLoading = true;
    this.historyError = '';

    if (this.useMockRecentUploads) {
      this.recentUploads = this.mockRecentUploads.slice(0, 25);
      localStorage.setItem(this.recentStorageKey, JSON.stringify(this.recentUploads));
      this.historyLoading = false;
      return;
    }

    const request = this.buildImportedFileRequest();
    this.claimService.getImportedFile(request).subscribe(
      (response) => {
        this.recentUploads = this.normalizeImportedFileResponse(response).slice(0, 25);
        localStorage.setItem(this.recentStorageKey, JSON.stringify(this.recentUploads));
        this.historyLoading = false;
      },
      () => {
        this.historyLoading = false;
        const value = localStorage.getItem(this.recentStorageKey);
        let cachedUploads: MultiClaim2RecentUpload[] = [];
        if (!value) {
          this.recentUploads = cachedUploads;
        } else {
          try {
            const parsed = JSON.parse(value);
            cachedUploads = Array.isArray(parsed) ? parsed : [];
          } catch {
            cachedUploads = [];
          }
          this.recentUploads = cachedUploads;
        }

        // On initial entry, a failed history fetch should not surface as an error.
        this.historyError = showErrorOnFailure
          ? 'Unable to load imported files right now.'
          : '';
      }
    );
  }

  private buildImportedFileRequest(): { PolicyNo: string; GroupCode: string | null; StartDate: string; EndDate: string } {
    const endDate = new Date();
    const startDate = new Date();
    startDate.setDate(endDate.getDate() - 30);
    const policy: any = this.loginService.getPolicyDetails() || {};

    return {
      PolicyNo: policy.policyNumber || '',
      GroupCode: this.getGroupCode() || null,
      StartDate: startDate.toISOString().slice(0, 10),
      EndDate: endDate.toISOString().slice(0, 10)
    };
  }

  private normalizeImportedFileResponse(response: any): MultiClaim2RecentUpload[] {
    let payload = response;
    if (typeof response === 'string') {
      try {
        payload = JSON.parse(response);
      } catch {
        return [];
      }
    }

    const rows = this.extractImportedFileRows(payload);
    return rows.map((item) => ({
      claimFileId: item && item.claimFileId !== undefined ? item.claimFileId : (item && item.claimFileID !== undefined ? item.claimFileID : ''),
      fileName: item && item.fileName ? item.fileName : '',
      blobGuid: item && item.blobGuid ? item.blobGuid : '',
      error: item && item.error ? item.error : '',
      importStatus: item && item.importStatus ? item.importStatus : '',
      importedOn: item && item.importedOn ? item.importedOn : '',
      importedBy: item && item.importedBy ? item.importedBy : '',
      policyNo: item && item.policyNo ? item.policyNo : '',
      gwccStatus: item && item.gwccStatus ? item.gwccStatus : '',
      groupCode: item && item.groupCode ? item.groupCode : ''
    }));
  }

  private extractImportedFileRows(payload: any): any[] {
    if (Array.isArray(payload)) {
      return payload;
    }

    if (!payload || typeof payload !== 'object') {
      return [];
    }

    if (Array.isArray(payload.data)) {
      return payload.data;
    }

    if (Array.isArray(payload.result)) {
      return payload.result;
    }

    if (Array.isArray(payload.items)) {
      return payload.items;
    }

    return [];
  }

  private getCurrentClaimFileId(): number | string | null {
    const fromLastExport = this.resolvePositiveIdentifier(this.lastExportFileImportId);
    if (fromLastExport !== null) {
      return fromLastExport;
    }

    const rowWithClaimFileId = this.rows.find((row) => this.resolvePositiveIdentifier(row && row.claimFileId) !== null);
    if (rowWithClaimFileId) {
      const resolvedRowClaimFileId = this.resolvePositiveIdentifier(rowWithClaimFileId.claimFileId);
      if (resolvedRowClaimFileId !== null) {
        return resolvedRowClaimFileId;
      }
    }

    return null;
  }

  private resolvePositiveIdentifier(value: any): number | null {
    if (value === null || value === undefined || value === '') {
      return null;
    }

    const parsed = Number(value);
    return Number.isFinite(parsed) && parsed > 0 ? parsed : null;
  }

  private handleAuthFailure(error: any): boolean {
    if (!this.isAuthFailure(error)) {
      return false;
    }

    this.systemError = 'Your session has expired. Please sign in again to continue.';

    if (this.authRecoveryInProgress) {
      return true;
    }

    this.authRecoveryInProgress = true;
    this.loginService.removeLocalStorage().subscribe({
      complete: () => {
        window.location.assign(`${ENV.baseUrl.web}auth`);
      }
    });

    return true;
  }

  private isAuthFailure(rawError: any): boolean {
    const error = this.unwrapError(rawError);
    if (!error) {
      return false;
    }

    const status = Number(error.status);
    const errorCode = String(error?.error?.error || '').toLowerCase();
    const description = String(error?.error?.error_description || '').toLowerCase();
    const bodyText = String(error?.error?.text || '').toLowerCase();
    // MSAL's silent-token-refresh failures can also surface directly as error.message (no nested error.error).
    const topLevelMessage = String(error?.message || '').toLowerCase();

    if (errorCode === 'invalid_grant' || errorCode === 'login_required' || errorCode === 'interaction_required'
      || topLevelMessage.indexOf('login_required') > -1 || topLevelMessage.indexOf('interaction_required') > -1
      || description.indexOf('invalid refresh token') > -1 || description.indexOf('unknown or invalid refresh token') > -1) {
      return true;
    }

    return status === 401 || (status === 403 && bodyText.indexOf('authorization') > -1);
  }

  private resetUploadFlow(message?: string): void {
    this.stopValidationPolling();
    this.selectedFile = null;
    this.rows = [];
    this.currentSubmissionNumber = '';
    this.lastExportFileImportId = null;
    this.reviewFilter = 'all';
    this.currentPage = 1;
    this.pageSize = 10;
    this.uploadingClaimIds = [];
    this.deletingDocumentIds = [];
    this.editingRowId = null;
    this.editingDraft = null;
    this.editingRowLoading = false;
    this.step = 2;

    if (message) {
      this.exportError = message;
    }
  }

  private refreshRowsForClaimFile(claimFileId: number | string): void {
    this.claimService.getReadOnlyList(claimFileId, this.getCurrentPolicyNumber()).subscribe(
      (response) => {
        const refreshedRows = this.mapRefreshDataRows(response);
        if (refreshedRows.length) {
          this.rows = refreshedRows;
          this.currentPage = 1;
          this.clampReviewPage();
        }
      },
      () => {
        // Keep existing rows if refresh fails.
      }
    );
  }

  private saveExportBlob(response: HttpResponse<Blob>, fileImportId: number | string): void {
    const contentType = response && response.headers ? (response.headers.get('Content-Type') || 'application/octet-stream') : 'application/octet-stream';
    const blobBody: BlobPart = response && response.body ? response.body : '';
    const blob = new Blob([blobBody], { type: contentType });
    const fallbackName = `MultiClaim - Submission #${fileImportId}.xlsx`;
    const contentDisposition = response && response.headers ? response.headers.get('Content-Disposition') : null;
    const fileName = contentDisposition ? this.getTemplateFileName(contentDisposition) : fallbackName;
    FileSaver.saveAs(blob, fileName);
  }

  private mapRefreshDataRows(response: any): MultiClaim2Row[] {
    const payload = this.extractClaimRows(response);
    if (!payload.length) {
      return [];
    }

    return payload.map((item: any, index: number) => {
      const merch = this.parseAmount(this.getFieldValue(item, ['merchandiseAmount', 'MerchandiseAmount']));
      const ship = this.parseAmount(this.getFieldValue(item, ['shippingAmount', 'ShippingAmount']));
      const totalRaw = this.getFieldValue(item, ['totalAmount', 'TotalAmount']);
      const total = totalRaw !== undefined && totalRaw !== null && String(totalRaw).trim() !== ''
        ? this.parseAmount(totalRaw)
        : (merch + ship);

      const extractedClaimId = this.getFieldValue(item, ['claimID', 'ClaimID', 'claimId', 'ClaimId', 'claimid']);
      const extractedClaimFileId = this.getFieldValue(item, ['claimFileID', 'ClaimFileID', 'ClaimFileId', 'claimFileId', 'claimfileid']);

      return {
        id: index + 1,
        selected: false,
        claimId: extractedClaimId,
        claimFileId: extractedClaimFileId,
        trackingNumber: String(this.getFieldValue(item, ['trackingNumber', 'TrackingNumber', 'trackingNumber_Ext', 'trackingNumberExt']) || ''),
        carrier: String(this.getFieldValue(item, ['transportationCarrier', 'TransportationCarrier', 'carrier', 'Carrier']) || ''),
        natureOfLoss: String(this.getFieldValue(item, ['natureOfLoss', 'NatureOfLoss', 'NatureofLoss']) || ''),
        merchandiseDescription: String(this.getFieldValue(item, ['merchandiseDescription', 'MerchandiseDescription']) || ''),
        shipDate: this.normalizeShipDate(this.getFieldValue(item, ['shipDate', 'ShipDate', 'lossDate', 'LossDate'])),
        merchandiseAmount: merch,
        shippingAmount: ship,
        totalAmount: total,
        additionalComments: String(this.getFieldValue(item, ['additionalComments', 'AdditionalComments']) || ''),
        ref1: String(this.getFieldValue(item, ['reF1', 'ref1', 'REF1']) || ''),
        creditMemo: String(this.getFieldValue(item, ['creditMemo', 'CreditMemo']) || ''),
        replacementTrackingNumber: String(this.getFieldValue(item, ['replacementTrackingNumber', 'ReplacementTrackingNumber']) || ''),
        draftClaimNo: String(this.getFieldValue(item, [
          'draftClaimNo', 'DraftClaimNo', 'draftClaimNumber', 'DraftClaimNumber', 'draftClaimNUM'
        ]) || ''),
        gwccClaimStatus: String(this.getFieldValue(item, [
          'gwccClaimStatus', 'GWCCClaimStatus', 'gwccStatus', 'GWCCStatus',
          'hvcmClaimStatus', 'HVCMClaimStatus', 'claimStatus', 'ClaimStatus'
        ]) || ''),
        status: String(this.getFieldValue(item, ['status', 'Status']) || ''),
        reasonForFailure: String(this.getFieldValue(item, ['reasonforFailure', 'reasonForFailure', 'ReasonforFailure', 'ReasonForFailure']) || ''),
        remarks: String(this.getFieldValue(item, ['remarks', 'Remarks', 'error', 'Error', 'errors', 'Errors', 'validationError', 'ValidationError', 'errorMessage', 'ErrorMessage', 'message', 'Message']) || ''),
        isValid: this.toNumberOrUndefined(this.getFieldValue(item, ['isValid', 'IsValid'])),
        documentCount: this.toNumberOrUndefined(this.getFieldValue(item, ['documentCount', 'DocumentCount'])),
        documents: []
      } as MultiClaim2Row;
    });
  }

  private extractClaimRows(response: any): any[] {
    if (Array.isArray(response)) {
      return response;
    }

    if (!response || typeof response !== 'object') {
      return [];
    }

    if (Array.isArray(response.response)) {
      return response.response;
    }

    if (Array.isArray(response.Response)) {
      return response.Response;
    }

    if (Array.isArray(response.data)) {
      return response.data;
    }

    if (response.data && Array.isArray(response.data.claims)) {
      return response.data.claims;
    }

    if (response.data && Array.isArray(response.data.Claims)) {
      return response.data.Claims;
    }

    if (Array.isArray(response.Data)) {
      return response.Data;
    }

    if (Array.isArray(response.claimFinalDataModel)) {
      return response.claimFinalDataModel;
    }

    if (Array.isArray(response.claimfinaldatamodel)) {
      return response.claimfinaldatamodel;
    }

    if (Array.isArray(response.claimFinalData)) {
      return response.claimFinalData;
    }

    if (Array.isArray(response.claims)) {
      return response.claims;
    }

    if (Array.isArray(response.claimDetails)) {
      return response.claimDetails;
    }

    if (Array.isArray(response.ClaimDetails)) {
      return response.ClaimDetails;
    }

    if (Array.isArray(response.ClaimFinalDataModel)) {
      return response.ClaimFinalDataModel;
    }

    if (Array.isArray(response.ClaimFinalData)) {
      return response.ClaimFinalData;
    }

    if (Array.isArray(response.Claims)) {
      return response.Claims;
    }

    if (response.claimFinalDataModel && typeof response.claimFinalDataModel === 'object') {
      const claimFinalDataModel = response.claimFinalDataModel;
      if (Array.isArray(claimFinalDataModel.response)) {
        return claimFinalDataModel.response;
      }
      if (Array.isArray(claimFinalDataModel.data)) {
        return claimFinalDataModel.data;
      }
      if (Array.isArray(claimFinalDataModel.result)) {
        return claimFinalDataModel.result;
      }
      if (Array.isArray(claimFinalDataModel.items)) {
        return claimFinalDataModel.items;
      }
    }

    if (response.claimfinaldatamodel && typeof response.claimfinaldatamodel === 'object') {
      const claimFinalDataModel = response.claimfinaldatamodel;
      if (Array.isArray(claimFinalDataModel.response)) {
        return claimFinalDataModel.response;
      }
      if (Array.isArray(claimFinalDataModel.data)) {
        return claimFinalDataModel.data;
      }
      if (Array.isArray(claimFinalDataModel.result)) {
        return claimFinalDataModel.result;
      }
      if (Array.isArray(claimFinalDataModel.items)) {
        return claimFinalDataModel.items;
      }
    }

    if (response.ClaimFinalDataModel && typeof response.ClaimFinalDataModel === 'object') {
      const claimFinalDataModel = response.ClaimFinalDataModel;
      if (Array.isArray(claimFinalDataModel.response)) {
        return claimFinalDataModel.response;
      }
      if (Array.isArray(claimFinalDataModel.data)) {
        return claimFinalDataModel.data;
      }
      if (Array.isArray(claimFinalDataModel.result)) {
        return claimFinalDataModel.result;
      }
      if (Array.isArray(claimFinalDataModel.items)) {
        return claimFinalDataModel.items;
      }
    }

    if (Array.isArray(response.items)) {
      return response.items;
    }

    if (Array.isArray(response.Items)) {
      return response.Items;
    }

    if (Array.isArray(response.result)) {
      return response.result;
    }

    if (Array.isArray(response.Result)) {
      return response.Result;
    }

    return [];
  }

  private getFieldValue(source: any, aliases: string[]): any {
    if (!source || typeof source !== 'object' || !aliases || !aliases.length) {
      return undefined;
    }

    for (const alias of aliases) {
      if (Object.prototype.hasOwnProperty.call(source, alias)) {
        const value = source[alias];
        if (value !== undefined && value !== null && value !== '') {
          return value;
        }
      }
    }

    const sourceKeys = Object.keys(source);
    for (const alias of aliases) {
      const normalizedAlias = this.normalizeFieldKey(alias);
      const matchingKey = sourceKeys.find((key) => this.normalizeFieldKey(key) === normalizedAlias);
      if (matchingKey) {
        const value = source[matchingKey];
        if (value !== undefined && value !== null && value !== '') {
          return value;
        }
      }
    }

    return undefined;
  }

  private normalizeFieldKey(value: string): string {
    return String(value || '').replace(/[^a-zA-Z0-9]/g, '').toLowerCase();
  }

  // ImportedOn is stored as UTC, but older API responses omit the timezone marker.
  // Return the real instant so Angular's date pipe displays it in the browser's local timezone.
  getImportedOnDate(value: string): Date | null {
    const raw = String(value || '').trim();
    if (!raw) {
      return null;
    }

    if (/[zZ]$|[+-]\d{2}:?\d{2}$/.test(raw)) {
      const direct = new Date(raw);
      return isNaN(direct.getTime()) ? null : direct;
    }

    const match = raw.match(/^(\d{4})-(\d{2})-(\d{2})[ T](\d{2}):(\d{2})(?::(\d{2}))?(?:\.(\d+))?/);
    if (!match) {
      const fallback = new Date(raw);
      return isNaN(fallback.getTime()) ? null : fallback;
    }

    const [, year, month, day, hour, minute, second, fraction] = match;
    const milliseconds = fraction ? Number(`0.${fraction}`) * 1000 : 0;
    return new Date(Date.UTC(+year, +month - 1, +day, +hour, +minute, second ? +second : 0, milliseconds));
  }

  private parseAmount(value: any): number {
    if (value === undefined || value === null || value === '') {
      return 0;
    }

    if (typeof value === 'number') {
      return Number.isFinite(value) ? value : 0;
    }

    const normalized = String(value).replace(/[$,\s]/g, '');
    const parsed = Number(normalized);
    return Number.isFinite(parsed) ? parsed : 0;
  }

  private isValidAmountInput(value: string): boolean {
    const normalized = value.replace(/[$,\s]/g, '');
    return /^[+-]?(?:\d+(?:\.\d*)?|\.\d+)$/.test(normalized);
  }

  // Normalize grid display only; keep the source/API value unchanged for save-format parity.
  getShipDateDisplay(row: MultiClaim2Row | null | undefined): string {
    const raw = String(row && row.shipDate ? row.shipDate : '').trim();
    if (!raw) {
      return '-';
    }

    const dateOnly = raw.replace(/\s+12:00:00\s*AM$/i, '');
    const isoDate = dateOnly.match(/^(\d{4})-(\d{1,2})-(\d{1,2})(?:$|[ T])/);
    const usDate = dateOnly.match(/^(\d{1,2})\/(\d{1,2})\/(\d{4})(?:$|\s)/);
    const match = isoDate || usDate;
    if (!match) {
      return dateOnly;
    }

    const year = Number(isoDate ? match[1] : match[3]);
    const month = Number(isoDate ? match[2] : match[1]);
    const day = Number(isoDate ? match[3] : match[2]);
    const parsed = new Date(Date.UTC(year, month - 1, day));
    if (parsed.getUTCFullYear() !== year || parsed.getUTCMonth() !== month - 1 || parsed.getUTCDate() !== day) {
      return dateOnly;
    }

    return `${month}/${day}/${year}`;
  }

  private normalizeShipDate(value: any): string {
    if (value === undefined || value === null) {
      return '';
    }

    if (value instanceof Date) {
      return this.formatDateMMDDYYYY(value);
    }

    if (typeof value === 'number' && Number.isFinite(value)) {
      return this.tryFormatExcelSerialDate(value) || String(value);
    }

    const raw = String(value).trim();
    if (!raw) {
      return '';
    }

    if (/^\d+(\.\d+)?$/.test(raw)) {
      const numeric = Number(raw);
      const formatted = this.tryFormatExcelSerialDate(numeric);
      if (formatted) {
        return formatted;
      }
    }

    // Legacy parity: non-numeric date strings are displayed as provided by source/API.
    return raw;
  }

  // The Edit Data date picker only yields a yyyy-MM-dd value with no time; reformat it to match
  // the M/D/YYYY h:mm:ss AM/PM format the grid otherwise shows for every other row's Ship Date.
  private formatEditedShipDate(value: string): string {
    const raw = String(value || '').trim();
    if (!/^\d{4}-\d{2}-\d{2}$/.test(raw)) {
      return this.normalizeShipDate(raw);
    }

    const [year, month, day] = raw.split('-').map((part) => Number(part));
    const date = new Date(year, month - 1, day);
    if (Number.isNaN(date.getTime())) {
      return this.normalizeShipDate(raw);
    }

    return `${month}/${day}/${year} 12:00:00 AM`;
  }

  private tryFormatExcelSerialDate(serial: number): string | null {
    if (!Number.isFinite(serial) || serial <= 0) {
      return null;
    }

    // Excel serial dates count days from 1899-12-30.
    const excelEpochUtcMs = Date.UTC(1899, 11, 30);
    const wholeDays = Math.floor(serial);
    const date = new Date(excelEpochUtcMs + wholeDays * 86400000);

    if (Number.isNaN(date.getTime())) {
      return null;
    }

    return this.formatDateMMDDYYYY(date);
  }

  private formatDateMMDDYYYY(date: Date): string {
    const month = date.getUTCMonth() + 1;
    const day = date.getUTCDate();
    const year = date.getUTCFullYear();
    return `${month}/${day}/${year}`;
  }

  private toNumberOrUndefined(value: any): number | undefined {
    if (value === undefined || value === null || value === '') {
      return undefined;
    }

    const parsed = Number(value);
    return Number.isFinite(parsed) ? parsed : undefined;
  }

  private getValidClaimId(row: MultiClaim2Row | null | undefined): number | null {
    const parsed = Number(row && row.claimId !== undefined && row.claimId !== null ? row.claimId : NaN);
    return Number.isFinite(parsed) && parsed > 0 ? parsed : null;
  }

  // Legacy Home.cshtml maps backend import states onto a smaller set of display labels.
  getRecentStatusLabel(status: string): string {
    switch ((status || '').toLowerCase()) {
      case 'accepted':
      case 'imported':
      case 'summary':
        return 'Pending';
      case 'finalized':
        return 'Accepted';
      case 'rejected':
        return 'Rejected';
      case 'cancelled':
      case 'canceled':
        return 'Cancelled';
      default:
        return 'Failed';
    }
  }

  getRecentStatusClass(status: string): string {
    switch (this.getRecentStatusLabel(status)) {
      case 'Pending':
        return 'status-pending';
      case 'Accepted':
        return 'status-accepted';
      case 'Cancelled':
        return 'status-cancelled';
      case 'Failed':
        return 'status-failed';
      default:
        return 'status-default';
    }
  }

  isFailedUpload(upload: MultiClaim2RecentUpload): boolean {
    return this.getRecentStatusLabel(upload && upload.importStatus) === 'Failed' && !!(upload && upload.error);
  }

  // The API's claimFileId identifies the import attempt; it becomes a user-facing submission number only
  // after the submission is finalized successfully.
  hasNoSubmissionNumber(upload: MultiClaim2RecentUpload): boolean {
    return this.getRecentStatusLabel(upload && upload.importStatus) !== 'Accepted';
  }

  getSubmissionNumberDisplay(upload: MultiClaim2RecentUpload): string {
    return this.hasNoSubmissionNumber(upload) ? '-' : (upload && upload.claimFileId ? String(upload.claimFileId) : '-');
  }

  showUploadError(upload: MultiClaim2RecentUpload): void {
    this.historyError = upload && upload.error ? String(upload.error) : '';
  }

  // Legacy Home.cshtml makes the file name clickable and routes by raw ImportStatus
  // (Failed -> error popup, Imported -> Review Data, Accepted -> Upload Documents,
  // Finalized/Summary -> Review & Submit, anything else (e.g. Cancelled) -> read-only ReadOnlyList page).
  openRecentUpload(upload: MultiClaim2RecentUpload): void {
    if (!upload) {
      return;
    }

    if (this.isFailedUpload(upload)) {
      this.showUploadError(upload);
      return;
    }

    const claimFileId = this.resolvePositiveIdentifier(upload.claimFileId);
    if (claimFileId === null) {
      this.historyError = 'Unable to open this submission right now.';
      return;
    }

    this.historyError = '';
    const rawStatus = (upload.importStatus || '').toLowerCase();
    const policyNo = upload.policyNo || this.getCurrentPolicyNumber();

    // Legacy opens accepted uploads through UploadDocument, which returns documentCount for the
    // paperclip badge. The read-only list endpoint does not reliably include that value on resume.
    if (rawStatus === 'accepted') {
      this.claimService.uploadDocumentClaims(this.buildUploadDocumentPayload(claimFileId)).subscribe(
        (response) => {
          const rows = this.mapRefreshDataRows(response);
          if (rows.length) {
            this.showRecentUpload(rows, claimFileId, rawStatus);
            return;
          }
          this.openRecentUploadFallback(claimFileId, policyNo, rawStatus);
        },
        (error) => {
          if (this.handleAuthFailure(error)) {
            return;
          }
          this.openRecentUploadFallback(claimFileId, policyNo, rawStatus);
        }
      );
      return;
    }

    // Finalized submissions use the submitted-list contract (legacy GetFinalClaimDetails); other
    // statuses use the general list (GetClaimDetails).
    const rowsRequest = rawStatus === 'finalized'
      ? this.claimService.getSubmittedList(claimFileId, policyNo)
      : this.claimService.getReadOnlyList(claimFileId, policyNo);

    rowsRequest.subscribe(
      (response) => {
        const rows = this.mapRefreshDataRows(response);
        if (!rows.length && rawStatus === 'finalized') {
          // GetFinalClaimDetails can come back empty for a submitted file; GetClaimDetails still has the rows.
          this.openRecentUploadFallback(claimFileId, policyNo, rawStatus);
          return;
        }

        this.showRecentUpload(rows, claimFileId, rawStatus);
      },
      (error) => {
        if (this.handleAuthFailure(error)) {
          return;
        }

        if (rawStatus === 'finalized') {
          this.openRecentUploadFallback(claimFileId, policyNo, rawStatus);
          return;
        }

        this.historyError = 'Unable to load this submission right now. Please try again.';
      }
    );
  }

  private openRecentUploadFallback(claimFileId: number, policyNo: string, rawStatus: string): void {
    this.claimService.getReadOnlyList(claimFileId, policyNo).subscribe(
      (response) => {
        this.showRecentUpload(this.mapRefreshDataRows(response), claimFileId, rawStatus);
      },
      (error) => {
        if (this.handleAuthFailure(error)) {
          return;
        }
        this.historyError = 'Unable to load this submission right now. Please try again.';
      }
    );
  }

  private showRecentUpload(rows: MultiClaim2Row[], claimFileId: number, rawStatus: string): void {
    this.rows = rows;
    this.lastExportFileImportId = claimFileId;
    this.currentPage = 1;
    this.docCurrentPage = 1;
    this.clampReviewPage();

    if (!rows.length) {
      this.historyError = 'No claim details are available for this submission.';
    }

    if (rawStatus === 'accepted') {
      this.isReadOnlyView = false;
      this.step = 4;
      this.refreshResumedDocumentCounts(claimFileId);
    } else if (rawStatus === 'summary') {
      this.isReadOnlyView = false;
      this.step = 5;
    } else if (rawStatus === 'imported') {
      this.isReadOnlyView = false;
      this.step = 3;
    } else {
      // Legacy routes "Finalized" (ShowFinishedClaim -> SubmittedList) and any other
      // terminal/unknown status (ShowCancel -> ReadOnlyList) to the same read-only completed view.
      this.isReadOnlyView = true;
    }
  }

  // uploadDocument can omit DocumentCount after a saved submission is reopened. The document
  // endpoint returns the persisted records with ClaimId, so rebuild each grid badge from that source.
  private refreshResumedDocumentCounts(claimFileId: number): void {
    this.claimService.getUploadedDocumentsDetails(claimFileId).subscribe(
      (response) => {
        const items = Array.isArray(response) ? response
          : (response && Array.isArray(response.data) ? response.data
            : (response && Array.isArray(response.response) ? response.response : []));
        const countsByClaimId = new Map<number, number>();

        items.forEach((item: any) => {
          const claimId = this.resolvePositiveIdentifier(this.getFieldValue(item, ['claimId', 'ClaimId', 'claimID', 'ClaimID']));
          const documentName = String(this.getFieldValue(item, ['documentName', 'DocumentName', 'name', 'Name']) || '').trim();
          const uploadedDate = this.getFieldValue(item, ['uploadedDate', 'UploadedDate']);
          if (claimId === null || !documentName || !uploadedDate) {
            return;
          }

          countsByClaimId.set(claimId, (countsByClaimId.get(claimId) || 0) + 1);
        });

        if (!countsByClaimId.size) {
          return;
        }

        this.rows = this.rows.map((row) => {
          const claimId = this.resolvePositiveIdentifier(row.claimId);
          const documentCount = claimId === null ? undefined : countsByClaimId.get(claimId);
          return documentCount === undefined ? row : { ...row, documentCount };
        });
      },
      () => {
        // The grid still uses DocumentCount from uploadDocument when this optional refresh is unavailable.
      }
    );
  }

  // Legacy ReadOnlyList's Home button returns to the Upload Submission Form / Recent Uploads landing.
  closeReadOnlyView(): void {
    this.isReadOnlyView = false;
    this.backToLanding();
  }
}